import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@/utils/apiError';
import { CountrySelect, StateSelect, TimezoneSelect, DialCodeSelect } from '@/components/UI/GeoSelect';
import { useAppSelector, useAppDispatch } from '@/hooks';
import {
  useGetTenantSettingsQuery,
  useUpdateTenantMutation,
  useSendInvitationMutation,
  useResendInvitationMutation,
  useListInvitationsQuery,
  useListAssignableRolesQuery,
  useListTenantUsersQuery,
  useGetTenantUserByIdQuery,
  useUpdateTenantUserMutation,
  useGetOnboardingStateQuery,
  useMarkSettingsVisitedMutation,
} from '@/redux/internal-services/onboardingApi';
import type { AssignableRole, TenantUser } from '@/redux/internal-services/onboardingApi';
import {
  useGetBrandConfigQuery,
  useUpdateBrandConfigMutation,
  useUploadLogoMutation,
} from '@/redux/internal-services/brandApi';
import {
  useListCatalogueEntriesQuery,
  useUpdateCatalogueConfigMutation,
} from '@/redux/internal-services/platformAdminApi';
import {
  useGetMeQuery,
  useUpdateMeMutation,
  useGetVoiceCatalogueConfigQuery,
  useUpdateVoiceTenantConfigMutation,
} from '@/redux/internal-services/usersApi';
import type { VoiceOption } from '@/redux/internal-services/usersApi';
import {
  useListCrewsQuery,
  useCreateCrewMutation,
  useUpdateCrewMutation,
  useDeleteCrewMutation,
} from '@/redux/internal-services/jobsApi';
import type { Crew, CrewBody } from '@/redux/internal-services/jobsApi';
import {
  useGetQuickBooksStatusQuery, useLazyGetQuickBooksAuthorizeUrlQuery, useDisconnectQuickBooksMutation,
} from '@/redux/internal-services/jobsApi';
import { setBrand } from '@/redux/slices/brandSlice';
import { applyTheme } from '@/utils/theme';
import { DEFAULT_BRAND } from '@/theme/brandDefaults';
import { brandColorPayload, ALL_COLOR_KEYS } from '@/theme/brandPayload';
import { normalizeHex } from '@/theme/colorMath';
import { Button } from '@/components/UI/Button';
import QuickBooksSyncSettings from '@/components/Settings/QuickBooksSyncSettings';
import { Modal } from '@/components/UI/Modal';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import VoiceInteractionPicker from '@/components/SAM/VoiceInteractionPicker';
import MaterialServiceManagementSection from '@/components/Settings/MaterialServiceManagementSection';
import ProposalContentSection from '@/components/Settings/ProposalContentSection';
import { Switch } from '@/components/UI/Switch';
import PageShell from '@/components/Layout/PageShell';
import { Tabs } from '@/components/UI/Tabs';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge } from '@/components/UI/Badge';
import { Chip } from '@/components/UI/Chip';
import { SearchInput } from '@/components/UI/SearchInput';
import { IconButton } from '@/components/UI/IconButton';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Alert } from '@/components/UI/Alert';
import { Select } from '@/components/UI/Select';
import WorkflowSettingsCard from '@/components/Workflow/WorkflowSettingsCard';
import { useListConfigurableWorkflowsQuery } from '@/redux/internal-services/workflowEngineApi';

// --- Types ---

type TopTab =
  | 'brand'
  | 'team'
  | 'teams'
  | 'sam'
  | 'integrations'
  | 'notifications'
  | 'pricing'
  | 'material_service_management'
  | 'workflow'
  | 'invoice'
  | 'proposal_template'
  | 'proposal_content'
  | 'operations';

type BrandSubTab =
  | 'identity'
  | 'logo'
  | 'contact'
  | 'address'
  | 'compliance'
  | 'social'
  | 'documents';

// --- Helpers ---

// --- Sub-components ---

const SectionSave: React.FC<{ loading?: boolean; label?: string }> = ({
  loading,
  label = 'Save changes',
}) => (
  <Button type="submit" loading={loading} size="md" className="mt-6">
    {label}
  </Button>
);

const FormField: React.FC<{
  label: string;
  children: React.ReactNode;
  hint?: string;
}> = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-medium text-ui-text mb-1 uppercase tracking-wide">
      {label}
    </label>
    {children}
    {hint && <p className="text-xs text-ui-text3 mt-1">{hint}</p>}
  </div>
);

const TextInput: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { fullWidth?: boolean; error?: boolean }
> = ({ fullWidth = true, error, className, ...props }) => (
  <input
    {...props}
    className={cn(fieldClass({ error }), !fullWidth && 'w-auto', className)}
  />
);

const TextArea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({
  className,
  ...props
}) => (
  <textarea
    {...props}
    className={cn(fieldClass({ shape: 'box' }), 'resize-none', className)}
  />
);

// --- Brand Settings Right Panel ---

const brandNavItems: { key: BrandSubTab; label: string }[] = [
  { key: 'identity', label: 'Identity' },
  { key: 'logo', label: 'Logo & Colors' },
  { key: 'contact', label: 'Contact Info' },
  { key: 'address', label: 'Address' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'social', label: 'Social' },
  { key: 'documents', label: 'Documents' },
];

const BrandRightPanel: React.FC<{
  activeSub: BrandSubTab;
  onNav: (s: BrandSubTab) => void;
}> = ({ activeSub, onNav }) => (
  <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
    <div className="card-surface p-5">
      <p className="text-sm font-semibold text-ui-text mb-1">Brand Settings</p>
      <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
        Your brand identity is applied to all client-facing documents — proposals,
        invoices, emails, and the client portal.
      </p>
      <nav className="space-y-1">
        {brandNavItems.map((item, i) => (
          <Button
            key={item.key}
            variant={activeSub === item.key ? 'accent' : 'ghost'}
            size="md"
            onClick={() => onNav(item.key)}
            className={cn(
              'w-full justify-start gap-3 text-left font-medium',
              activeSub !== item.key && 'text-ui-text'
            )}
          >
            <span
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                activeSub === item.key
                  ? 'bg-white text-accent-text'
                  : 'bg-accent-fill text-on-accent'
              )}
            >
              {i + 1}
            </span>
            {item.label}
          </Button>
        ))}
      </nav>
    </div>
  </aside>
);

// --- Brand Header ---

const BrandHeader: React.FC<{ companyName: string }> = ({ companyName }) => {
  const { data: brandConfig } = useGetBrandConfigQuery();

  const primary = brandConfig?.primary_color || DEFAULT_BRAND.primaryColor;
  const accent = brandConfig?.accent_color || DEFAULT_BRAND.accentColor;
  const success = brandConfig?.success_color || DEFAULT_BRAND.successColor;
  const logoUrl = brandConfig?.logo_light_url || '';

  const swatches = [
    { label: 'Primary', value: primary },
    { label: 'Accent', value: accent },
    { label: 'Success', value: success },
  ];

  return (
    <div
      className="flex items-center gap-4 mb-6 rounded-control px-5 py-8"
      style={{ background: `linear-gradient(to right, ${primary}1A, ${accent}1A)` }}
    >
      {logoUrl ? (
        <div className="flex h-12 w-12 items-center justify-center rounded-control bg-white shrink-0 overflow-hidden border border-white/60">
          <img src={logoUrl} alt={companyName || 'Logo'} className="h-full w-full object-contain p-1" />
        </div>
      ) : (
        <div
          className="flex h-12 w-12 items-center justify-center rounded-control text-white text-h3 shrink-0"
          style={{ background: primary }}
        >
          {companyName ? companyName.slice(0, 2).toUpperCase() : '—"'}
        </div>
      )}
      <div>
        <p className="font-semibold text-ui-text">{companyName || 'Your Company'}</p>
        <div className="flex items-center gap-2 mt-1.5">
          {swatches.map((c) => (
            <span
              key={c.label}
              className="h-4 w-4 rounded-full border border-white/60 shadow-card shrink-0"
              style={{ background: c.value }}
              title={`${c.label}: ${c.value.toUpperCase()}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Brand Sub-Tabs ---

const BrandSubTabBar: React.FC<{
  active: BrandSubTab;
  onChange: (t: BrandSubTab) => void;
}> = ({ active, onChange }) => (
  <Tabs
    tabs={brandNavItems.map((item) => ({ id: item.key, label: item.label }))}
    value={active}
    onChange={(id) => onChange(id as BrandSubTab)}
    label="Brand settings sections"
    className="hidden lg:flex mb-6"
  />
);

// --- Identity Tab ---

const IdentityTab: React.FC<{
  values: Record<string, string>;
  onChange: (k: string, v: string) => void;
  onSave: (e: React.FormEvent) => void;
  loading?: boolean;
}> = ({ values, onChange, onSave, loading }) => (
  <form onSubmit={onSave} className="space-y-5">
    <FormField label="Company Name" hint="Your trading or display name shown to clients.">
      <TextInput
        placeholder="Enter company name"
        value={values.company_name}
        onChange={(e) => onChange('company_name', e.target.value)}
      />
    </FormField>
    <FormField label="Legal Name" hint="Your registered legal business name.">
      <TextInput
        placeholder="Enter legal name"
        value={values.legal_name}
        onChange={(e) => onChange('legal_name', e.target.value)}
      />
    </FormField>
    <FormField label="Tagline" hint="Used in proposals and public listings.">
      <TextInput
        placeholder="Enter tagline"
        value={values.tagline}
        onChange={(e) => onChange('tagline', e.target.value)}
      />
    </FormField>
    <FormField label="About">
      <TextArea
        rows={4}
        placeholder="Quality roofing since 1995"
        value={values.about}
        onChange={(e) => onChange('about', e.target.value)}
      />
    </FormField>
    <SectionSave loading={loading} />
  </form>
);

// --- Logo & Colors Tab ---

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

const LogoSlot: React.FC<{
  label: string;
  hint: string;
  currentUrl?: string;
  onUpload: (file: File) => void;
  uploading?: boolean;
  error?: string;
}> = ({ label, hint, currentUrl, onUpload, uploading, error }) => {
  // Use a unique id so multiple LogoSlots on the same page each link to their own input.
  const inputId = useRef(`logo-slot-${Math.random().toString(36).slice(2)}`).current;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    onUpload(file);
  };

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Use a native <label htmlFor> so clicking always triggers the file dialog via the
          browser's own label mechanism — more reliable than a programmatic .click() call,
          which can be blocked when the browser's user-activation state has been consumed
          (e.g. while an AudioContext is active in interactive voice mode). */}
      <label
        htmlFor={inputId}
        className={cn(
          'flex h-24 w-full sm:w-36 items-center justify-center rounded-control border-2 border-dashed bg-ui-raised cursor-pointer transition overflow-hidden',
          uploading ? 'cursor-default opacity-70' : '',
          error ? 'border-danger' : 'border-ui-border hover:border-accent'
        )}
      >
        {uploading ? (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        ) : currentUrl ? (
          <img src={currentUrl} alt={label} className="h-full w-full object-contain p-2" />
        ) : (
          <div className="text-center">
            <svg className="mx-auto h-6 w-6 text-ui-text3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-xs text-ui-text3 mt-1">Click to upload</p>
          </div>
        )}
      </label>
      <input
        id={inputId}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        className="hidden"
        disabled={uploading}
        onChange={handleChange}
      />
      <p className="text-xs font-medium text-ui-text">{label}</p>
      <p className="text-xs text-ui-text3">{hint}</p>
      {error && <p className="text-xs text-danger-text text-center max-w-[9rem]">{error}</p>}
    </div>
  );
};

const ColorSwatch: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-ui-text">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-9 rounded-chip cursor-pointer border border-ui-border p-0.5"
      />
      <TextInput
        value={value.toUpperCase()}
        onChange={(e) => onChange(e.target.value)}
        fullWidth={false}
        className="w-full sm:w-28 uppercase"
        placeholder="#000000"
      />
    </div>
  </div>
);

interface BrandColorSet {
  /** Dark canvas the whole surface/text ramp and the always-dark sidebar chrome derive from. */
  base: string;
  primary: string;
  accent: string;
  success: string;
}

const DEFAULT_BRAND_COLORS: BrandColorSet = {
  base: DEFAULT_BRAND.baseColor,
  primary: DEFAULT_BRAND.primaryColor,
  accent: DEFAULT_BRAND.accentColor,
  success: DEFAULT_BRAND.successColor,
};

const LogoColorsTab: React.FC<{ onThemeChange?: () => void }> = ({ onThemeChange }) => {
  const dispatch = useAppDispatch();
  const { data: brandConfig } = useGetBrandConfigQuery();
  const [updateBrandConfig, { isLoading: savingColors }] = useUpdateBrandConfigMutation();
  const [uploadLogo] = useUploadLogoMutation();

  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
  const [slotErrors, setSlotErrors] = useState<Record<string, string>>({});
  const [localUrls, setLocalUrls] = useState<Record<string, string>>({});

  const [colors, setColors] = useState({
    base: brandConfig?.base_color ?? DEFAULT_BRAND_COLORS.base,
    primary: brandConfig?.primary_color ?? DEFAULT_BRAND_COLORS.primary,
    accent: brandConfig?.accent_color ?? DEFAULT_BRAND_COLORS.accent,
    success: brandConfig?.success_color ?? DEFAULT_BRAND_COLORS.success,
  });
  const colorsDirtyRef = useRef(false);

  useEffect(() => {
    if (!brandConfig || colorsDirtyRef.current) return;
    setColors({
      base: brandConfig.base_color || DEFAULT_BRAND_COLORS.base,
      primary: brandConfig.primary_color || DEFAULT_BRAND_COLORS.primary,
      accent: brandConfig.accent_color || DEFAULT_BRAND_COLORS.accent,
      success: brandConfig.success_color || DEFAULT_BRAND_COLORS.success,
    });
  }, [brandConfig]);

  const isDefaultColors =
    colors.base.toUpperCase() === DEFAULT_BRAND_COLORS.base &&
    colors.primary.toUpperCase() === DEFAULT_BRAND_COLORS.primary &&
    colors.accent.toUpperCase() === DEFAULT_BRAND_COLORS.accent &&
    colors.success.toUpperCase() === DEFAULT_BRAND_COLORS.success;
  const [colorError, setColorError] = useState('');

  // Persist + apply a color set everywhere (saved config, live CSS theme,
  // and the Redux brand slice the sidebar/header read from).
  // A colour equal to the platform default is cleared rather than saved (brandColorPayload), so the tenant keeps
  // inheriting the default instead of pinning a copy of it.
  const persistColors = async (next: BrandColorSet) => {
    await updateBrandConfig(
      brandColorPayload({ base_color: next.base, primary_color: next.primary, accent_color: next.accent, success_color: next.success }),
    ).unwrap();
    colorsDirtyRef.current = false;
    applyTheme({ base_color: next.base, primary_color: next.primary, accent_color: next.accent, success_color: next.success });
    dispatch(setBrand({ baseColor: next.base, primaryColor: next.primary, accentColor: next.accent, successColor: next.success }));
    onThemeChange?.();
  };

  // Reset clears the tenant's colour overrides so the platform defaults take over, and applies them immediately
  // so the whole app (including the sidebar) reflects the change without a separate save.
  const handleResetColors = async () => {
    setColorError('');
    try {
      await updateBrandConfig({ unset: ALL_COLOR_KEYS }).unwrap();
      colorsDirtyRef.current = false;
      setColors({ ...DEFAULT_BRAND_COLORS });
      applyTheme({
        base_color: DEFAULT_BRAND_COLORS.base,
        primary_color: DEFAULT_BRAND_COLORS.primary,
        accent_color: DEFAULT_BRAND_COLORS.accent,
        success_color: DEFAULT_BRAND_COLORS.success,
      });
      dispatch(
        setBrand({
          baseColor: DEFAULT_BRAND_COLORS.base,
          primaryColor: DEFAULT_BRAND_COLORS.primary,
          accentColor: DEFAULT_BRAND_COLORS.accent,
          successColor: DEFAULT_BRAND_COLORS.success,
        }),
      );
      onThemeChange?.();
    } catch (err: unknown) {
      setColorError(err instanceof Error ? err.message : 'Failed to reset colors');
    }
  };

  const logoLightUrl = localUrls['logo_light_url'] ?? brandConfig?.logo_light_url ?? '';
  const logoDarkUrl = localUrls['logo_dark_url'] ?? brandConfig?.logo_dark_url ?? '';
  const faviconUrl = localUrls['favicon_url'] ?? brandConfig?.favicon_url ?? '';

  const handleLogoUpload = async (file: File, field: 'logo_light_url' | 'logo_dark_url' | 'favicon_url') => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setSlotErrors((prev) => ({ ...prev, [field]: 'Unsupported file type. Use JPEG, PNG, GIF, WebP, or SVG.' }));
      return;
    }
    setSlotErrors((prev) => ({ ...prev, [field]: '' }));
    setUploadingSlot(field);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = await uploadLogo(fd).unwrap();
      await updateBrandConfig({ [field]: url }).unwrap();
      setLocalUrls((prev) => ({ ...prev, [field]: url }));
      dispatch(setBrand(
        field === 'logo_light_url' ? { logoLightUrl: url } :
        field === 'logo_dark_url' ? { logoDarkUrl: url } :
        { faviconUrl: url }
      ));
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleColorSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // Free-typed hex is validated here: a partial value like "#4E" used to reach the theme engine as NaN.
    const bad = (['base', 'primary', 'accent', 'success'] as const).filter((k) => !normalizeHex(colors[k]));
    if (bad.length) {
      setColorError(`Enter a full hex colour for ${bad.join(', ')} (for example ${DEFAULT_BRAND_COLORS.primary}).`);
      return;
    }
    setColorError('');
    try {
      await persistColors({
        base: normalizeHex(colors.base)!,
        primary: normalizeHex(colors.primary)!,
        accent: normalizeHex(colors.accent)!,
        success: normalizeHex(colors.success)!,
      });
    } catch (err: unknown) {
      setColorError(err instanceof Error ? err.message : 'Failed to save colors. Please try again.');
    }
  };

  return (
    <div className="space-y-7">
      <div>
        <p className="text-sm font-semibold text-ui-text mb-1">Brand &amp; logo</p>
        <p className="text-xs text-ui-text3 mb-4">Upload your logos —" changes apply immediately.</p>
        <div className="flex gap-6 flex-wrap">
          <LogoSlot
            label="Logo (light bg)"
            hint="512 × 512 px"
            currentUrl={logoLightUrl}
            onUpload={(f) => handleLogoUpload(f, 'logo_light_url')}
            uploading={uploadingSlot === 'logo_light_url'}
            error={slotErrors['logo_light_url']}
          />
          <LogoSlot
            label="Logo (dark bg)"
            hint="512 × 512 px"
            currentUrl={logoDarkUrl}
            onUpload={(f) => handleLogoUpload(f, 'logo_dark_url')}
            uploading={uploadingSlot === 'logo_dark_url'}
            error={slotErrors['logo_dark_url']}
          />
          <LogoSlot
            label="Favicon"
            hint="64 × 64 px"
            currentUrl={faviconUrl}
            onUpload={(f) => handleLogoUpload(f, 'favicon_url')}
            uploading={uploadingSlot === 'favicon_url'}
            error={slotErrors['favicon_url']}
          />
        </div>
      </div>

      <form onSubmit={handleColorSave} className="space-y-5">
        <div>
          <p className="text-sm font-semibold text-ui-text mb-1">Brand Colors</p>
          <p className="text-xs text-ui-text3 mb-4">Colors update the entire app UI instantly on save.</p>
          <div className="flex gap-6 flex-wrap">
            <ColorSwatch label="Base" value={colors.base} onChange={(v) => { colorsDirtyRef.current = true; setColors((p) => ({ ...p, base: v })); }} />
            <ColorSwatch label="Primary" value={colors.primary} onChange={(v) => { colorsDirtyRef.current = true; setColors((p) => ({ ...p, primary: v })); }} />
            <ColorSwatch label="Accent" value={colors.accent} onChange={(v) => { colorsDirtyRef.current = true; setColors((p) => ({ ...p, accent: v })); }} />
            <ColorSwatch label="Success" value={colors.success} onChange={(v) => { colorsDirtyRef.current = true; setColors((p) => ({ ...p, success: v })); }} />
          </div>
          {colorError && (
            <Alert tone="danger" className="mt-3">
              {colorError}
            </Alert>
          )}
        </div>

        {/* Live preview strip —" hidden for now
        <div className="flex items-center gap-3 p-4 rounded-control border border-ui-border bg-ui-subtle ">
          <div className="h-8 w-8 rounded-chip shrink-0" style={{ background: colors.primary }} />
          <div className="h-8 w-8 rounded-chip shrink-0" style={{ background: colors.accent }} />
          <div className="h-8 w-8 rounded-chip shrink-0" style={{ background: colors.success }} />
          <span className="text-xs text-ui-text3 ml-2">Live preview</span>
          <button
            type="button"
            className="ml-auto text-xs px-3 py-1.5 rounded-chip border text-ui-text border-ui-border hover:border-accent transition"
            style={{ borderColor: colors.primary }}
            onClick={() => applyTheme({ primary_color: colors.primary, accent_color: colors.accent, success_color: colors.success })}
          >
            Preview in app
          </button>
        </div>
        */}

        <div className="flex items-center gap-3 mt-6">
          <Button type="submit" loading={savingColors} size="md">
            Save Colors
          </Button>
          <Button
            type="button"
            variant="outline"
            size="md"
            disabled={isDefaultColors}
            onClick={handleResetColors}
          >
            Reset to default
          </Button>
        </div>
      </form>
    </div>
  );
};

// --- URL normalization ---

const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const isValidUrl = (value: string): boolean => {
  if (!value) return true; // empty is allowed
  try {
    const url = new URL(value);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
};

// --- Contact Tab ---

const ContactTab: React.FC<{
  values: Record<string, string>;
  onChange: (k: string, v: string) => void;
  onSave: (e: React.FormEvent) => void;
  loading?: boolean;
}> = ({ values, onChange, onSave, loading }) => {
  const [urlError, setUrlError] = React.useState('');

  const handleWebsiteBlur = (raw: string) => {
    const normalized = normalizeUrl(raw);
    onChange('website', normalized);
    setUrlError(isValidUrl(normalized) ? '' : 'Enter a valid URL (e.g. yoursite.com)');
  };

  return (
    <form onSubmit={onSave} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField label="Contact Email">
          <TextInput
            type="email"
            placeholder="contact@example.com"
            value={values.contact_email}
            onChange={(e) => onChange('contact_email', e.target.value)}
          />
        </FormField>
        <FormField label="Support Email">
          <TextInput
            type="email"
            placeholder="support@example.com"
            value={values.support_email}
            onChange={(e) => onChange('support_email', e.target.value)}
          />
        </FormField>
        <FormField label="Phone">
          <TextInput
            type="tel"
            placeholder="+1 (555) 000-0000"
            value={values.phone}
            onChange={(e) => onChange('phone', e.target.value)}
          />
        </FormField>
        <FormField label="Website">
          <TextInput
            type="text"
            placeholder="yoursite.com"
            value={values.website}
            onChange={(e) => { onChange('website', e.target.value); setUrlError(''); }}
            onBlur={(e) => handleWebsiteBlur(e.target.value)}
            error={!!urlError}
          />
          {urlError && <p className="text-xs text-danger-text mt-1">{urlError}</p>}
        </FormField>
      </div>
      <SectionSave loading={loading} />
    </form>
  );
};

// --- Address Tab ---

const AddressTab: React.FC<{
  values: Record<string, string>;
  onChange: (k: string, v: string) => void;
  onSave: (e: React.FormEvent) => void;
  loading?: boolean;
}> = ({ values, onChange, onSave, loading }) => {
  // Once the user picks a timezone explicitly (via the Timezone dropdown) we
  // stop auto-deriving it from the selected country/state. Start as false so a
  // location change still updates the timezone even when one was saved at
  // registration —" only an explicit edit on this screen pins it.
  const tzManual = useRef(false);

  // If no timezone is set at all, default to the admin's browser timezone so a
  // sensible value is always present even before a location is chosen.
  useEffect(() => {
    if (!values.timezone) {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz) onChange('timezone', browserTz);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoTz = (tz?: string) => {
    if (tz && !tzManual.current) onChange('timezone', tz);
  };

  return (
    <form onSubmit={onSave} className="space-y-5">
      <FormField label="Street">
        <TextInput
          placeholder="123 Main St"
          value={values.street}
          onChange={(e) => onChange('street', e.target.value)}
        />
      </FormField>
      <FormField label="Country">
        <CountrySelect
          value={values.country_code || values.country}
          onChange={(code, name, tz) => {
            onChange('country_code', code);
            onChange('country', name);
            autoTz(tz);
          }}
          onResolve={(code, name) => {
            // Normalize legacy data (name-only / mis-cased) to the ISO code so
            // the State / Province dropdown can load states for this country.
            onChange('country_code', code);
            onChange('country', name);
          }}
        />
      </FormField>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="col-span-2">
          <FormField label="City">
            <TextInput
              placeholder="City"
              value={values.city}
              onChange={(e) => onChange('city', e.target.value)}
            />
          </FormField>
        </div>
        <FormField label="State / Province">
          <StateSelect
            countryCode={values.country_code}
            value={values.state}
            onChange={(code, _name, tz) => { onChange('state', code); autoTz(tz); }}
          />
        </FormField>
        <FormField label="ZIP">
          <TextInput
            placeholder="ZIP"
            value={values.zip}
            onChange={(e) => onChange('zip', e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Timezone">
        <TimezoneSelect
          value={values.timezone}
          onChange={(tz) => { tzManual.current = true; onChange('timezone', tz); }}
        />
      </FormField>
      <SectionSave loading={loading} />
    </form>
  );
};

// --- Compliance Tab ---

const ComplianceTab: React.FC<{
  values: Record<string, string>;
  onChange: (k: string, v: string) => void;
  onSave: (e: React.FormEvent) => void;
  loading?: boolean;
}> = ({ values, onChange, onSave, loading }) => (
  <form onSubmit={onSave} className="space-y-5">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <FormField label="License #">
        <TextInput
          placeholder="License number"
          value={values.license_number}
          onChange={(e) => onChange('license_number', e.target.value)}
        />
      </FormField>
      <FormField label="License State">
        <TextInput
          placeholder="State"
          value={values.license_state}
          onChange={(e) => onChange('license_state', e.target.value)}
        />
      </FormField>
      <FormField label="Insurance Policy">
        <TextInput
          placeholder="Policy number"
          value={values.insurance_policy}
          onChange={(e) => onChange('insurance_policy', e.target.value)}
        />
      </FormField>
      <FormField label="DAL / Tax ID">
        <TextInput
          placeholder="Tax ID"
          value={values.tax_id}
          onChange={(e) => onChange('tax_id', e.target.value)}
        />
      </FormField>
    </div>
    <FormField label="Insurance Expiry Block">
      <TextInput
        placeholder="Expiry date"
        value={values.insurance_expiry}
        onChange={(e) => onChange('insurance_expiry', e.target.value)}
      />
    </FormField>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <FormField label="Service Areas">
        <TextInput
          placeholder="Raleigh, Durham, Chapel Hill"
          value={values.service_areas}
          onChange={(e) => onChange('service_areas', e.target.value)}
        />
      </FormField>
      <FormField label="Certifications">
        <TextInput
          placeholder="GAF Master Elite, CertainTeed"
          value={values.certifications}
          onChange={(e) => onChange('certifications', e.target.value)}
        />
      </FormField>
    </div>
    <SectionSave loading={loading} />
  </form>
);

// --- Social Tab ---

const SOCIAL_FIELDS = [
  { key: 'facebook', label: 'Facebook', placeholder: 'facebook.com/yourpage' },
  { key: 'instagram', label: 'Instagram', placeholder: 'instagram.com/yourhandle' },
  { key: 'linkedin', label: 'LinkedIn / Twitter & X', placeholder: 'linkedin.com/company/...' },
  { key: 'youtube', label: 'YouTube', placeholder: 'youtube.com/@yourchannel' },
  { key: 'google_business', label: 'Google Business', placeholder: 'google.com/maps/...' },
];

const SocialTab: React.FC<{
  values: Record<string, string>;
  onChange: (k: string, v: string) => void;
  onSave: (e: React.FormEvent) => void;
  loading?: boolean;
}> = ({ values, onChange, onSave, loading }) => {
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const handleBlur = (key: string, raw: string) => {
    const normalized = normalizeUrl(raw);
    onChange(key, normalized);
    setErrors((prev) => ({
      ...prev,
      [key]: isValidUrl(normalized) ? '' : 'Enter a valid URL (e.g. sitename.com/yourpage)',
    }));
  };

  return (
    <form onSubmit={onSave} className="space-y-5">
      {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
        <FormField key={key} label={label}>
          <TextInput
            type="text"
            placeholder={placeholder}
            value={values[key]}
            onChange={(e) => { onChange(key, e.target.value); setErrors((p) => ({ ...p, [key]: '' })); }}
            onBlur={(e) => handleBlur(key, e.target.value)}
            error={!!errors[key]}
          />
          {errors[key] && <p className="text-xs text-danger-text mt-1">{errors[key]}</p>}
        </FormField>
      ))}
      <SectionSave loading={loading} />
    </form>
  );
};

// --- Documents Tab ---

const DocumentsTab: React.FC<{
  values: Record<string, string>;
  onChange: (k: string, v: string) => void;
  onSave: (e: React.FormEvent) => void;
  loading?: boolean;
}> = ({ values, onChange, onSave, loading }) => (
  <form onSubmit={onSave} className="space-y-5">
    <FormField label="Proposal Footer" hint="Appears at the bottom of every generated proposal.">
      <TextArea
        rows={3}
        placeholder="Thank you for considering us..."
        value={values.proposal_footer}
        onChange={(e) => onChange('proposal_footer', e.target.value)}
      />
    </FormField>
    <FormField label="Invoice Footer">
      <TextArea
        rows={3}
        placeholder="Payment terms..."
        value={values.invoice_footer}
        onChange={(e) => onChange('invoice_footer', e.target.value)}
      />
    </FormField>
    <FormField label="Default Email Signature">
      <TextArea
        rows={4}
        placeholder="Best regards,..."
        value={values.email_signature}
        onChange={(e) => onChange('email_signature', e.target.value)}
      />
    </FormField>
    <SectionSave loading={loading} />
  </form>
);

// --- Brand Settings (full section) ---

const BrandSettingsSection: React.FC<{
  settings: Record<string, unknown>;
  onSave: (section: string, data: Record<string, unknown>) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [sub, setSub] = useState<BrandSubTab>('identity');

  const s = settings as Record<string, string>;

  const [identity, setIdentity] = useState({
    company_name: s.company_name ?? '',
    legal_name: s.legal_name ?? '',
    tagline: s.tagline ?? '',
    about: s.about ?? '',
  });


  const [contact, setContact] = useState({
    contact_email: s.contact_email ?? '',
    support_email: s.support_email ?? '',
    phone: s.phone ?? '',
    website: s.website ?? '',
  });

  const addr = ((s.address ?? {}) as unknown) as Record<string, string>;
  const [address, setAddress] = useState({
    street: addr.street ?? '',
    city: addr.city ?? '',
    state: addr.state ?? '',
    zip: addr.zip ?? '',
    country: addr.country ?? '',
    country_code: addr.country_code ?? '',
    // Timezone is the tenant-level default (tenant.timezone) —" the single source
    // of truth used since registration. Surfaced here alongside the address.
    timezone: s.timezone ?? addr.timezone ?? '',
  });

  const [compliance, setCompliance] = useState({
    license_number: s.license_number ?? '',
    license_state: s.license_state ?? '',
    insurance_policy: s.insurance_policy ?? '',
    insurance_expiry: s.insurance_expiry ?? '',
    tax_id: s.tax_id ?? '',
    service_areas: s.service_areas ?? '',
    certifications: s.certifications ?? '',
  });

  const [social, setSocial] = useState<Record<string, string>>(
    (settings.social_links as Record<string, string>) ?? {}
  );

  const [documents, setDocuments] = useState({
    proposal_footer: s.proposal_footer ?? '',
    invoice_footer: s.invoice_footer ?? '',
    email_signature: s.email_signature ?? '',
  });

  const patch =
    (setter: React.Dispatch<React.SetStateAction<Record<string, string>>>) =>
    (k: string, v: string) =>
      setter((prev) => ({ ...prev, [k]: v }));

  const handleSave =
    (section: string, data: Record<string, unknown>) =>
    async (e: React.FormEvent) => {
      e.preventDefault();
      await onSave(section, data);
    };

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0">
        <BrandHeader companyName={identity.company_name} />
        <BrandSubTabBar active={sub} onChange={setSub} />

        {sub === 'identity' && (
          <IdentityTab
            values={identity}
            onChange={patch(setIdentity as unknown as React.Dispatch<React.SetStateAction<Record<string, string>>>)}
            onSave={handleSave('profile', identity)}
            loading={saving}
          />
        )}
        {sub === 'logo' && (
          <LogoColorsTab />
        )}
        {sub === 'contact' && (
          <ContactTab
            values={contact}
            onChange={patch(setContact as unknown as React.Dispatch<React.SetStateAction<Record<string, string>>>)}
            onSave={handleSave('contact', contact)}
            loading={saving}
          />
        )}
        {sub === 'address' && (
          <AddressTab
            values={address}
            onChange={patch(setAddress as unknown as React.Dispatch<React.SetStateAction<Record<string, string>>>)}
            onSave={handleSave('address', { address, timezone: address.timezone })}
            loading={saving}
          />
        )}
        {sub === 'compliance' && (
          <ComplianceTab
            values={compliance}
            onChange={patch(setCompliance as unknown as React.Dispatch<React.SetStateAction<Record<string, string>>>)}
            onSave={handleSave('compliance', compliance)}
            loading={saving}
          />
        )}
        {sub === 'social' && (
          <SocialTab
            values={social}
            onChange={patch(setSocial as unknown as React.Dispatch<React.SetStateAction<Record<string, string>>>)}
            onSave={handleSave('social', { social_links: social })}
            loading={saving}
          />
        )}
        {sub === 'documents' && (
          <DocumentsTab
            values={documents}
            onChange={patch(setDocuments as unknown as React.Dispatch<React.SetStateAction<Record<string, string>>>)}
            onSave={handleSave('documents', documents)}
            loading={saving}
          />
        )}
      </div>
      <BrandRightPanel activeSub={sub} onNav={setSub} />
    </div>
  );
};

// --- Team & Roles Section ---

const Toggle: React.FC<{ checked?: boolean }> = ({ checked = false }) => {
  const [on, setOn] = useState(checked);
  return <Switch checked={on} onCheckedChange={setOn} aria-label="Toggle" />;
};

const MEMBERSHIP_STATUSES = ['Active', 'Suspended', 'Inactive'];

const initialsOf = (label: string) =>
  label
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// Status-aware badge styling —" dot + pill tint per membership status.
const USER_STATUS_STYLES: Record<string, { dot: string; variant: 'success' | 'danger' | 'neutral' }> = {
  Active: { dot: 'bg-success', variant: 'success' },
  Suspended: { dot: 'bg-danger', variant: 'danger' },
  Inactive: { dot: 'bg-steel', variant: 'neutral' },
};

// How many role chips to show before collapsing the rest into a "+N" pill.
const ROLE_CHIP_CAP = 2;

// Shimmer placeholder row shown while the first page of users is loading.
const UserRowSkeleton: React.FC = () => (
  <tr>
    <td className="td-cell">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-ui-border3 animate-pulse shrink-0" />
        <span className="h-3 w-28 rounded bg-ui-border3 animate-pulse" />
      </div>
    </td>
    <td className="td-cell"><span className="block h-3 w-40 rounded bg-ui-border3 animate-pulse" /></td>
    <td className="td-cell"><span className="block h-5 w-16 rounded-full bg-ui-border3 animate-pulse" /></td>
    <td className="td-cell"><span className="block h-5 w-14 rounded-full bg-ui-border3 animate-pulse" /></td>
  </tr>
);

// Shimmer placeholder card shown on mobile while the first page of users loads.
const USERS_EMPTY_ICON = (
  <svg className="h-9 w-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a3 3 0 10-2.5-4.5" />
  </svg>
);

const UserCardSkeleton: React.FC = () => (
  <div className="card-surface p-4">
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-full bg-ui-border3 animate-pulse shrink-0" />
      <div className="flex-1 space-y-2">
        <span className="block h-3 w-28 rounded bg-ui-border3 animate-pulse" />
        <span className="block h-3 w-40 rounded bg-ui-border3 animate-pulse" />
      </div>
    </div>
  </div>
);
// ─── Edit User Modal ────────────────────────────────────────────────────────

const EDIT_STATUS_OPTIONS = ['Active', 'Suspended', 'Inactive'];

const EditUserModal: React.FC<{
  user: TenantUser;
  roles: AssignableRole[];
  onClose: () => void;
}> = ({ user, roles, onClose }) => {
  const { t } = useTranslation();
  const [updateTenantUser, { isLoading }] = useUpdateTenantUserMutation();
  const { data: me } = useGetMeQuery();
  const isEditingSelf = me?.user_id === user.user_id;

  const [name, setName] = useState(user.name ?? '');
  const [phone, setPhone] = useState(user.phone ?? '');
  const [selectedRoles, setSelectedRoles] = useState<string[]>(user.role_ids ?? []);
  const [status, setStatus] = useState(
    user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Active'
  );
  const [error, setError] = useState('');

  const toggleRole = (id: string) => {
    // Prevent removing own Tenant Admin chip — the backend will reject it anyway,
    // but blocking it here gives immediate feedback.
    const role = roles.find((r) => r.id === id);
    if (isEditingSelf && role?.system_role_mappings?.includes('tenant_admin') && selectedRoles.includes(id)) {
      return; // silently guard — the locked chip already shows the note below
    }
    setSelectedRoles((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await updateTenantUser({
        user_id: user.user_id,
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        role_ids: selectedRoles,
        status,
      }).unwrap();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'Failed to update user. Please try again.'));
    }
  };

  return (
    <Modal
      title="Edit Team Member"
      onClose={onClose}
      maxWidthClassName="max-w-md"
      footer={
        <>
          <Button type="button" variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="edit-team-member-form" loading={isLoading} size="md">
            Save changes
          </Button>
        </>
      }
    >
        <p className="text-xs text-ui-text2 truncate">{user.email}</p>

        <form id="edit-team-member-form" onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ui-text mb-1 uppercase tracking-wide">Name</label>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text mb-1 uppercase tracking-wide">Phone</label>
            <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter phone number" />
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text mb-2 uppercase tracking-wide">Roles</label>
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => {
                const active = selectedRoles.includes(r.id);
                const isTenantAdminChip = r.system_role_mappings?.includes('tenant_admin');
                const isLocked = isEditingSelf && isTenantAdminChip;
                return (
                  <Chip
                    key={r.id}
                    selected={active}
                    onClick={() => toggleRole(r.id)}
                    disabled={isLocked}
                    className={cn(isLocked && 'cursor-not-allowed')}
                  >
                    {r.name}
                    {isLocked && (
                      <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 shrink-0" aria-hidden="true">
                        <path d="M11 7V5a3 3 0 10-6 0v2H4a1 1 0 00-1 1v6a1 1 0 001 1h8a1 1 0 001-1V8a1 1 0 00-1-1h-1zm-4-2a1 1 0 112 0v2H7V5zm1 6a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    )}
                  </Chip>
                );
              })}
            </div>
            {isEditingSelf && roles.some((r) => r.system_role_mappings?.includes('tenant_admin')) && (
              <p className="mt-2 text-xs text-ui-text3 flex items-center gap-1">
                <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 shrink-0 text-ui-text3" aria-hidden="true">
                  <path d="M11 7V5a3 3 0 10-6 0v2H4a1 1 0 00-1 1v6a1 1 0 001 1h8a1 1 0 001-1V8a1 1 0 00-1-1h-1zm-4-2a1 1 0 112 0v2H7V5zm1 6a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
                Your own Tenant Admin access cannot be removed.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text mb-1 uppercase tracking-wide">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={fieldClass()}
            >
              {EDIT_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {error && <Alert tone="danger">{error}</Alert>}
        </form>
    </Modal>
  );
};

// Lists accepted members of the tenant (not pending invitations) with search +
// role/status filters. Reads GET /api/v1/users.
const PAGE_SIZE = 20;

const TenantUsersTable: React.FC<{ roles: AssignableRole[] }> = ({ roles }) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);

  // Debounce the free-text search so we don't refetch on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  // Reset to page 1 when filters change.
  useEffect(() => { setPage(1); }, [role, status]);

  const { data, isFetching } = useListTenantUsersQuery({
    search: debounced || undefined,
    role: role || undefined,
    status: status || undefined,
    page,
    page_size: PAGE_SIZE,
  });

  const users = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasFilters = Boolean(search || role || status);

  return (
    <div>
      {/* Sticky toolbar — pinned while scrolling the member list */}
      <div className="sticky top-0 z-sticky py-4 flex items-center justify-between gap-3 flex-wrap bg-ui/95 backdrop-blur border-b border-ui-border">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-ui-text">Users</p>
          {!isFetching && total > 0 && (
            <Badge className="font-semibold">{total}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName="w-full sm:w-60"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            aria-label="Filter by role"
            className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
          >
            <option value="">All roles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
            className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
          >
            <option value="">All statuses</option>
            {MEMBERSHIP_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Desktop / tablet: full table. Hidden on phones (cards below). No overflow wrapper here —
          the thead uses position: sticky, which a nested scroll container would break/clip. */}
      <div className="table-card mt-4 hidden overflow-clip sm:block">
        <table className="w-full text-sm">
          <thead>
            {/* Sticky below the pinned toolbar (top offset ≈ toolbar height). */}
            <tr className="[&>th]:sticky [&>th]:top-[68px] [&>th]:z-10 [&>th]:bg-ui-subtle">
              <th className="th-cell">Member</th>
              <th className="th-cell">Contact</th>
              <th className="th-cell">Roles</th>
              <th className="th-cell">Status</th>
              <th className="th-cell" />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              isFetching ? (
                <>
                  <UserRowSkeleton />
                  <UserRowSkeleton />
                  <UserRowSkeleton />
                </>
              ) : (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      icon={USERS_EMPTY_ICON}
                      title="No users found"
                      description={hasFilters ? 'Try adjusting your search or filters.' : 'Invited members appear here once they accept.'}
                    />
                  </td>
                </tr>
              )
            ) : (
              users.map((u) => {
                const shownRoles = u.role_names.slice(0, ROLE_CHIP_CAP);
                const extraRoles = u.role_names.length - shownRoles.length;
                const st = USER_STATUS_STYLES[u.status] ?? USER_STATUS_STYLES.Inactive;
                return (
                  <tr key={u.user_id} className="transition-colors hover:bg-ui-hover">
                    <td className="td-cell">
                      <div className="flex items-center gap-3 min-w-0">
                        {u.profile_picture_url ? (
                          <img src={u.profile_picture_url} alt="" className="h-9 w-9 rounded-full object-cover shrink-0 ring-1 ring-black/5" />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-accent-text text-xs font-semibold shrink-0">
                            {initialsOf(u.name || u.email || '?')}
                          </span>
                        )}
                        <span className="font-medium text-ui-text truncate">{u.name || '—"'}</span>
                      </div>
                    </td>
                    <td className="td-cell text-ui-text2">
                      <p className="truncate">{u.email}</p>
                      {u.phone && <p className="text-xs text-ui-text3 truncate">{u.phone}</p>}
                    </td>
                    <td className="td-cell">
                      <div className="flex flex-wrap items-center gap-1">
                        {u.role_names.length === 0 ? (
                          <span className="text-ui-text3">—"</span>
                        ) : (
                          <>
                            {shownRoles.map((rn) => (
                              <Badge key={rn}>{rn}</Badge>
                            ))}
                            {extraRoles > 0 && (
                              <Badge variant="neutral" title={u.role_names.join(', ')}>
                                +{extraRoles}
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="td-cell">
                      <Badge variant={st.variant} className="gap-1.5">
                        <span className={cn('h-1.5 w-1.5 rounded-full', st.dot)} />
                        {u.status}
                      </Badge>
                    </td>
                    <td className="td-cell text-right">
                      <Button type="button" variant="soft" size="xs" onClick={() => setEditingUser(u)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards — a 4-column table is unreadable squeezed into a phone width. */}
      <div className="mt-4 space-y-3 sm:hidden">
        {users.length === 0 ? (
          isFetching ? (
            <>
              <UserCardSkeleton />
              <UserCardSkeleton />
              <UserCardSkeleton />
            </>
          ) : (
            <div className="card-surface">
              <EmptyState
                icon={USERS_EMPTY_ICON}
                title="No users found"
                description={hasFilters ? 'Try adjusting your search or filters.' : 'Invited members appear here once they accept.'}
              />
            </div>
          )
        ) : (
          users.map((u) => {
            const shownRoles = u.role_names.slice(0, ROLE_CHIP_CAP);
            const extraRoles = u.role_names.length - shownRoles.length;
            const st = USER_STATUS_STYLES[u.status] ?? USER_STATUS_STYLES.Inactive;
            return (
              <div key={u.user_id} className="card-surface p-4">
                <div className="flex items-start gap-3">
                  {u.profile_picture_url ? (
                    <img src={u.profile_picture_url} alt="" className="h-10 w-10 rounded-full object-cover shrink-0 ring-1 ring-black/5" />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent-text text-xs font-semibold shrink-0">
                      {initialsOf(u.name || u.email || '?')}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-ui-text truncate">{u.name || '—'}</p>
                    <p className="text-sm text-ui-text2 truncate">{u.email}</p>
                    {u.phone && <p className="text-xs text-ui-text3 truncate">{u.phone}</p>}
                  </div>
                  <Badge variant={st.variant} className="shrink-0 gap-1.5">
                    <span className={cn('h-1.5 w-1.5 rounded-full', st.dot)} />
                    {u.status}
                  </Badge>
                </div>
                {u.role_names.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-1">
                    {shownRoles.map((rn) => (
                      <Badge key={rn}>{rn}</Badge>
                    ))}
                    {extraRoles > 0 && (
                      <Badge variant="neutral" title={u.role_names.join(', ')}>
                        +{extraRoles}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-ui-text2">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} users
          </p>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
            >
              Previous
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
              .reduce<(number | '...')[]>((acc, n, idx, arr) => {
                if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push('...');
                acc.push(n);
                return acc;
              }, [])
              .map((n, idx) =>
                n === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-xs text-ui-text3">…</span>
                ) : (
                  <Button
                    key={n}
                    type="button"
                    variant={page === n ? 'accent' : 'outline'}
                    size="xs"
                    onClick={() => setPage(n as number)}
                    disabled={isFetching}
                    aria-current={page === n ? 'page' : undefined}
                  >
                    {n}
                  </Button>
                ),
              )}
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || isFetching}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles}
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  );
};

const TeamSection: React.FC = () => {
  const { t } = useTranslation();
  const { data: rolesData } = useListAssignableRolesQuery();
  const [sendInvitation, { isLoading: sending }] = useSendInvitationMutation();
  const [resendInvitation, { isLoading: resending }] = useResendInvitationMutation();
  const { data: invData, refetch: refetchInvites } = useListInvitationsQuery({});

  const roles = rolesData?.data ?? [];
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dialCode, setDialCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [resendingId, setResendingId] = useState<string | null>(null);

  // The email is delivered in the background, so the immediate response can't
  // report success/failure. Pull the resolved delivery_status shortly after.
  const pollDelivery = () => {
    setTimeout(() => refetchInvites(), 3000);
    setTimeout(() => refetchInvites(), 8000);
  };

  const initials = (label: string) =>
    label
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();

  // Map a tenant_role_id â†' role name so listed invitations show a human label.
  const roleNameById = useMemo(
    () => new Map(roles.map((r) => [r.id, r.name])),
    [roles]
  );

  const roleNames = (ids: unknown): string =>
    (Array.isArray(ids) ? ids : [])
      .map((id) => roleNameById.get(id as string) ?? (id as string))
      .join(', ');

  const toggleRole = (id: string) =>
    setSelectedRoles((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || selectedRoles.length === 0) return;
    setError('');
    try {
      await sendInvitation({
        invitee_name: name,
        invitee_email: email,
        invitee_phone: phone ? `${dialCode}${phone}` : undefined,
        roles: selectedRoles,
      }).unwrap();
      setSent(true);
      setName('');
      setEmail('');
      setPhone('');
      setSelectedRoles([]);
      pollDelivery();
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      setError(apiErrorMessage(err, t, 'Failed to send invitation. Please try again.'));
    }
  };

  const handleResend = async (id: string) => {
    setResendingId(id);
    setError('');
    try {
      await resendInvitation(id).unwrap();
      setSent(true);
      pollDelivery();
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      setError(apiErrorMessage(err, t, 'Failed to resend invitation. Please try again.'));
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0 space-y-8">
        {/* Invite form */}
        <div>
          <p className="text-sm font-semibold text-ui-text mb-4">Team Members</p>
          <form
            onSubmit={handleSend}
            className="card-surface p-6 space-y-5"
          >
            <p className="text-sm font-semibold text-accent-text">Add new Team member</p>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1.5">Name</label>
              <TextInput
                type="text"
                placeholder="Enter team member name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1.5">
                Email Address <span className="text-danger-text">*</span>
              </label>
              <TextInput
                type="email"
                placeholder="email@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Mobile Phone */}
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1.5">Mobile Phone Number</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <DialCodeSelect value={dialCode} onChange={setDialCode} />
                <TextInput
                  type="tel"
                  placeholder="Enter phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>

            {/* Role pills */}
            <div>
              <label className="block text-sm font-medium text-ui-text mb-2">Roles</label>
              <div className="flex flex-wrap gap-2">
                {roles.map((r) => {
                  const active = selectedRoles.includes(r.id);
                  return (
                    <Chip
                      key={r.id}
                      selected={active}
                      onClick={() => toggleRole(r.id)}
                      title={r.name}
                      className="gap-2 px-4 py-1.5 text-sm"
                    >
                      {active && (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/20 text-xs font-semibold">
                          {initials(r.name)}
                        </span>
                      )}
                      {r.name}
                    </Chip>
                  );
                })}
              </div>
            </div>

            {sent && (
              <Alert tone="success">
                Invitation saved delivering the email in the background…
              </Alert>
            )}

            {error && <Alert tone="danger">{error}</Alert>}

            <Button
              type="submit"
              loading={sending}
              disabled={!email || selectedRoles.length === 0}
              className="w-full"
            >
              + Add Team Member
            </Button>
            <p className="text-xs text-ui-text3">
              The invited team member will get an invite link (password-protected) to their
              account.
            </p>
          </form>

          {/* Pending invitations — only show non-terminal statuses */}
          {invData?.data && (invData.data as Record<string, unknown>[]).filter(
            (inv) => !['Accepted', 'Revoked', 'Expired'].includes(inv.status as string)
          ).length > 0 && (
            <div className="mt-6">
              <p className="eyebrow mb-2">Pending Invitations</p>
              <div className="space-y-2">
                {(invData.data as Record<string, unknown>[]).filter(
                  (inv) => !['Accepted', 'Revoked', 'Expired'].includes(inv.status as string)
                ).map((inv) => {
                  const invId = (inv.invitation_id ?? inv.id) as string;
                  const failed = inv.delivery_status === 'failed';
                  const delivering = inv.delivery_status === 'pending';
                  return (
                  <div key={invId} className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3 text-sm rounded-control border border-ui-border bg-ui-subtle px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-ui-text font-medium truncate">
                        {(inv.invitee_name as string) || ((inv.invitee_email ?? inv.email) as string)}
                      </p>
                      <p className="text-xs text-ui-text2 truncate">
                        {[inv.invitee_email ?? inv.email, inv.invitee_phone]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      {roleNames(inv.roles) && (
                        <p className="text-xs text-ui-text3 truncate">{roleNames(inv.roles)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={failed ? 'danger' : 'accent'}>
                        {failed ? 'Email failed' : delivering ? 'Sending…' : 'Pending'}
                      </Badge>
                      <Button
                        type="button"
                        variant="soft"
                        size="xs"
                        onClick={() => handleResend(invId)}
                        disabled={resending && resendingId === invId}
                      >
                        {resending && resendingId === invId ? 'Resending…' : 'Resend'}
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Tenant users directory */}
        <TenantUsersTable roles={roles} />
      </div>
    </div>
  );
};

// --- SAM Settings Section ---

const SAMSection: React.FC<{ isTenantAdmin: boolean }> = ({ isTenantAdmin }) => {
  // --- User preferences ---
  const { data: me } = useGetMeQuery();
  const [updateMe, { isLoading: savingUser }] = useUpdateMeMutation();

  const [mode, setMode] = useState<'text' | 'voice'>('voice');
  const [voiceGender, setVoiceGender] = useState<'male' | 'female' | null>('female');
  const [hydrated, setHydrated] = useState(false);
  const [userSaved, setUserSaved] = useState(false);

  useEffect(() => {
    if (hydrated || !me) return;
    setMode(me.interaction_mode || 'voice');
    setVoiceGender(me.voice_gender ?? 'female');
    setHydrated(true);
  }, [me, hydrated]);

  const userDirty =
    hydrated &&
    (mode !== (me?.interaction_mode || 'voice') ||
      (mode === 'voice' && voiceGender !== (me?.voice_gender ?? 'female')));

  const handleSaveUser = async () => {
    await updateMe({
      interaction_mode: mode,
      voice_gender: mode === 'voice' ? voiceGender ?? undefined : undefined,
    }).unwrap().catch(() => {});
    setUserSaved(true);
    setTimeout(() => setUserSaved(false), 4000);
  };

  // --- Tenant defaults (admin only) ---
  const { data: voiceCatalogue } = useGetVoiceCatalogueConfigQuery(undefined, { skip: !isTenantAdmin });
  const [updateTenantVoice, { isLoading: savingTenant }] = useUpdateVoiceTenantConfigMutation();

  const availableVoices: VoiceOption[] = voiceCatalogue?.effective_value?.available_voices ?? [];
  const tenantVal = voiceCatalogue?.tenant_value ?? {};
  const effectiveVal = voiceCatalogue?.effective_value;

  const [tenantDefaultMode, setTenantDefaultMode] = useState<'text' | 'voice'>('text');
  const [tenantDefaultVoice, setTenantDefaultVoice] = useState<string>('alloy');
  const [tenantAllowedModes, setTenantAllowedModes] = useState<Array<'text' | 'voice'>>(['text', 'voice']);
  const [tenantHydrated, setTenantHydrated] = useState(false);
  const [tenantSaved, setTenantSaved] = useState(false);

  useEffect(() => {
    if (tenantHydrated || !voiceCatalogue) return;
    setTenantDefaultMode((tenantVal.interaction_mode_default ?? effectiveVal?.interaction_mode_default) || 'text');
    setTenantDefaultVoice((tenantVal.default_voice ?? effectiveVal?.default_voice) || 'alloy');
    setTenantAllowedModes((tenantVal.allowed_interaction_modes ?? effectiveVal?.allowed_interaction_modes) || ['text', 'voice']);
    setTenantHydrated(true);
  }, [voiceCatalogue, tenantHydrated, tenantVal, effectiveVal]);

  const tenantDirty =
    tenantHydrated && (
      tenantDefaultMode !== ((tenantVal.interaction_mode_default ?? effectiveVal?.interaction_mode_default) || 'text') ||
      tenantDefaultVoice !== ((tenantVal.default_voice ?? effectiveVal?.default_voice) || 'alloy') ||
      JSON.stringify(tenantAllowedModes.slice().sort()) !== JSON.stringify(((tenantVal.allowed_interaction_modes ?? effectiveVal?.allowed_interaction_modes) || ['text', 'voice']).slice().sort())
    );

  const handleSaveTenant = async () => {
    await updateTenantVoice({
      interaction_mode_default: tenantDefaultMode,
      default_voice: tenantDefaultVoice,
      allowed_interaction_modes: tenantAllowedModes,
    }).unwrap().catch(() => {});
    setTenantSaved(true);
    setTimeout(() => setTenantSaved(false), 4000);
  };

  const toggleAllowedMode = (m: 'text' | 'voice') => {
    setTenantAllowedModes((prev) => {
      if (prev.includes(m)) {
        // Keep at least one mode enabled
        if (prev.length === 1) return prev;
        return prev.filter((x) => x !== m);
      }
      return [...prev, m];
    });
  };

  const samSideItems = [
    'Voice & Interaction',
    'Choose a voice',
    'Voice training',
    'Optimize prompt rules',
    'Assistant tools',
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <div className="flex-1 min-w-0 space-y-6">
        {/* â"€â"€ My Preferences â"€â"€ */}
        <div>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-2">
            <div>
              <p className="text-sm font-semibold text-ui-text">My SAM Preferences</p>
              <p className="text-xs text-ui-text3">Customize how you personally interact with SAM.</p>
            </div>
            <Button
              size="sm"
              loading={savingUser}
              disabled={!userDirty || (mode === 'voice' && !voiceGender)}
              onClick={handleSaveUser}
            >
              Save
            </Button>
          </div>
        </div>

        <div className="card-surface overflow-hidden">
          <div className="border-b border-ui-border px-5 py-4">
            <h3 className="text-h3 text-ui-text">Voice &amp; Interaction</h3>
          </div>
          <div className="p-5">
            <p className="text-xs text-ui-text3 mb-4 leading-relaxed">
              Choose how you'd like to interact with SAM. In Voice mode, pick whether SAM sounds
              male or female.
            </p>
            <VoiceInteractionPicker
              mode={mode}
              voiceGender={voiceGender}
              onModeChange={setMode}
              onVoiceGenderChange={setVoiceGender}
            />
            {userSaved && <Alert tone="success" className="mt-4">Preferences saved.</Alert>}
          </div>
        </div>

        {/* â"€â"€ Tenant Defaults (admin only) â"€â"€ */}
        {isTenantAdmin && (
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ui-text">Tenant Defaults</p>
                <p className="text-xs text-ui-text3">Set SAM defaults for all users in your organisation.</p>
              </div>
              <Button
                size="sm"
                loading={savingTenant}
                disabled={!tenantDirty}
                onClick={handleSaveTenant}
              >
                Save Defaults
              </Button>
            </div>

            <div className="card-surface overflow-hidden">
              <div className="border-b border-ui-border px-5 py-4">
                <h3 className="text-h3 text-ui-text">Allowed Interaction Modes</h3>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-xs text-ui-text3 leading-relaxed">
                  Control which interaction modes are available to users in your organisation.
                  At least one mode must remain enabled.
                </p>
                <div className="flex gap-3">
                  {(['text', 'voice'] as const).map((m) => (
                    <Chip
                      key={m}
                      selected={tenantAllowedModes.includes(m)}
                      onClick={() => toggleAllowedMode(m)}
                      className="gap-2 px-4 py-2 text-sm"
                    >
                      <span className={cn(
                        'h-4 w-4 rounded-chip border flex items-center justify-center',
                        tenantAllowedModes.includes(m) ? 'bg-accent-fill border-accent' : 'border-ui-border'
                      )}>
                        {tenantAllowedModes.includes(m) && (
                          <svg viewBox="0 0 12 12" fill="none" className="w-2.5 h-2.5">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      {m === 'text' ? 'Text Only' : 'Voice & Text'}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>

            <div className="card-surface overflow-hidden">
              <div className="border-b border-ui-border px-5 py-4">
                <h3 className="text-h3 text-ui-text">Default Interaction Mode</h3>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-xs text-ui-text3 leading-relaxed">
                  The interaction mode applied to new users who have not set a preference.
                </p>
                <div className="flex gap-3">
                  {(['text', 'voice'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      disabled={!tenantAllowedModes.includes(m)}
                      onClick={() => setTenantDefaultMode(m)}
                      className={`flex-1 text-left border rounded-control p-4 transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        tenantDefaultMode === m
                          ? 'border-accent ring-2 ring-ui-ring/30 bg-accent-soft'
                          : 'border-ui-border hover:border-ui-border3'
                      }`}
                    >
                      <p className="text-sm font-semibold text-ui-text">
                        {m === 'text' ? 'Text Only' : 'Voice & Text'}
                      </p>
                      <p className="text-xs text-ui-text2 mt-1">
                        {m === 'text' ? 'Users type to chat with SAM.' : 'Users can talk to SAM and hear replies.'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {tenantAllowedModes.includes('voice') && availableVoices.length > 0 && (
              <div className="card-surface overflow-hidden">
                <div className="border-b border-ui-border px-5 py-4">
                <h3 className="text-h3 text-ui-text">Default Voice</h3>
              </div>
                <div className="p-5 space-y-3">
                  <p className="text-xs text-ui-text3 leading-relaxed">
                    The voice SAM uses for new users who haven't chosen one.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {availableVoices.map((v) => (
                      <div
                        key={v.id}
                        onClick={() => setTenantDefaultVoice(v.id)}
                        className={`cursor-pointer border rounded-control p-3 flex items-center gap-3 transition ${
                          tenantDefaultVoice === v.id
                            ? 'border-accent ring-2 ring-ui-ring/30 bg-accent-soft'
                            : 'border-ui-border hover:border-ui-border3'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ui-text">
                            {v.name} <span className="text-xs font-normal text-ui-text3">· {v.gender}</span>
                          </p>
                          <p className="text-xs text-ui-text2 mt-0.5">{v.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tenantSaved && <Alert tone="success">Tenant defaults saved.</Alert>}
          </div>
        )}

      </div>

      {/* Right panel */}
      <aside className="w-full lg:w-64 shrink-0 order-first lg:order-none">
        <div className="card-surface p-5">
          <p className="text-sm font-semibold text-ui-text mb-1">Voice &amp; Interaction</p>
          <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
            Configure how SAM responds to you —" text-only or voice —" and which voice it uses across
            your portal.
          </p>
          <nav className="space-y-1">
            {samSideItems.map((item, i) => (
              <div
                key={item}
                className="flex items-center gap-3 px-3 py-2 rounded-chip text-sm text-ui-text"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-fill text-on-accent text-xs font-semibold">
                  {i + 1}
                </span>
                {item}
              </div>
            ))}
          </nav>
        </div>
      </aside>
    </div>
  );
};

// --- Integrations Section ---

interface Integration {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: React.ReactNode;
}

const integrations: Integration[] = [
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Sync your Google Calendar events and appointments directly with your CRM to streamline scheduling.',
    color: '#EA4335',
    icon: (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.5 4H17V2.5a.5.5 0 00-1 0V4H8V2.5a.5.5 0 00-1 0V4H4.5A2.5 2.5 0 002 6.5v13A2.5 2.5 0 004.5 22h15a2.5 2.5 0 002.5-2.5v-13A2.5 2.5 0 0019.5 4zM4.5 5H7v1.5a.5.5 0 001 0V5h8v1.5a.5.5 0 001 0V5h2.5A1.5 1.5 0 0121 6.5V8H3V6.5A1.5 1.5 0 014.5 5zm15 16h-15A1.5 1.5 0 013 19.5V9h18v10.5A1.5 1.5 0 0119.5 21z" />
      </svg>
    ),
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks Online',
    description: 'Automatically sync invoices, payments, and customer data between QuickBooks and your CRM.',
    color: '#2CA01C',
    icon: (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93V18c2.76-.45 5-2.69 5.45-5.45H17c-.45 2.19-2.19 3.93-4.38 4.38zM11 6.07V8c-2.19.45-3.93 2.19-4.38 4.38H5.07C5.52 9.52 7.52 6.52 11 6.07zM6.62 13.62C7.07 15.81 8.81 17.55 11 18v-1.93c-1.19-.39-2.23-1.31-2.62-2.45H6.62zM17.38 12h1.55c-.45-2.76-2.69-5-5.45-5.45V8c2.19.45 3.93 2.19 4.38 4.38H17.38z" />
      </svg>
    ),
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Process payments, manage subscriptions, and track revenue directly within your platform.',
    color: '#635BFF',
    icon: (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" />
      </svg>
    ),
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Send notifications, alerts and updates to your team channels through Slack for seamless communication.',
    color: '#4A154B',
    icon: (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M5.042 15.165a2.528 2.528 0 01-2.52 2.523A2.528 2.528 0 010 15.165a2.527 2.527 0 012.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 012.521-2.52 2.527 2.527 0 012.521 2.52v6.313A2.528 2.528 0 018.834 24a2.528 2.528 0 01-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 01-2.521-2.52A2.528 2.528 0 018.834 0a2.528 2.528 0 012.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 012.521 2.521 2.528 2.528 0 01-2.521 2.521H2.522A2.528 2.528 0 010 8.834a2.528 2.528 0 012.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 012.522-2.521A2.528 2.528 0 0124 8.834a2.528 2.528 0 01-2.522 2.521h-2.522V8.834zM17.687 8.834a2.528 2.528 0 01-2.521 2.521 2.527 2.527 0 01-2.521-2.521V2.522A2.527 2.527 0 0115.166 0a2.528 2.528 0 012.521 2.522v6.312zM15.166 18.956a2.528 2.528 0 012.521 2.522A2.528 2.528 0 0115.166 24a2.527 2.527 0 01-2.521-2.522v-2.522h2.521zM15.166 17.687a2.527 2.527 0 01-2.521-2.521 2.526 2.526 0 012.521-2.521h6.312A2.527 2.527 0 0124 15.166a2.528 2.528 0 01-2.522 2.521h-6.312z" />
      </svg>
    ),
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Automate workflows by connecting your apps and services to create powerful integrations.',
    color: '#FF4A00',
    icon: (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14.786 9.914L18.929 5.77a.657.657 0 01.929.928l-4.143 4.143A6.593 6.593 0 0114.786 9.914zm-5.572 4.172L5.071 18.23a.657.657 0 01-.929-.929l4.143-4.143a6.593 6.593 0 011.929.928zM12 8.571a3.429 3.429 0 110 6.858A3.429 3.429 0 0112 8.571z" />
      </svg>
    ),
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    description: 'Send transactional and marketing emails at scale with reliable email delivery service.',
    color: '#1A82E2',
    icon: (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
      </svg>
    ),
  },
];

const IntegrationsSection: React.FC = () => {
  const { t } = useTranslation();
  const [connected, setConnected] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Integration | null>(null);
  const [qbMessage, setQbMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // QuickBooks is a real connection (OAuth via Intuit); the other cards are placeholders for now.
  const { data: qbStatus } = useGetQuickBooksStatusQuery();
  const [fetchAuthorizeUrl, { isFetching: qbStarting }] = useLazyGetQuickBooksAuthorizeUrlQuery();
  const [disconnectQuickBooks, { isLoading: qbDisconnecting }] = useDisconnectQuickBooksMutation();
  const qbConnected = !!qbStatus?.connected;
  const qbReconnect = qbStatus?.state === 'reconnect_required';

  // Intuit sends the tenant admin back here with ?qb_connected=true or ?qb_error=<reason>.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('qb_connected') === 'true') setQbMessage({ type: 'success', text: t('settings.quickbooks.connected', 'QuickBooks is connected.') });
    else if (params.get('qb_error')) setQbMessage({ type: 'error', text: t('settings.quickbooks.connectFailed', 'QuickBooks could not be connected: {{reason}}', { reason: params.get('qb_error') }) });
    else return;
    ['qb_connected', 'qb_error'].forEach((k) => params.delete(k));
    const rest = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- read the redirect params once, on mount

  const connectQuickBooks = async () => {
    setQbMessage(null);
    try {
      const url = await fetchAuthorizeUrl().unwrap();
      window.location.assign(url);
    } catch {
      setQbMessage({ type: 'error', text: t('settings.quickbooks.unavailable', 'QuickBooks is not available to connect right now.') });
    }
  };

  const isConnected = (id: string) => (id === 'quickbooks' ? qbConnected : connected.has(id));
  const toggleConnect = (id: string) => {
    if (id === 'quickbooks') {
      if (qbConnected) {
        void disconnectQuickBooks().unwrap()
          .then(() => setQbMessage({ type: 'success', text: t('settings.quickbooks.disconnected', 'QuickBooks disconnected.') }))
          .catch(() => setQbMessage({ type: 'error', text: t('settings.quickbooks.disconnectFailed', 'Could not disconnect QuickBooks.') }));
      } else void connectQuickBooks();
      return;
    }
    setConnected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const busyFor = (id: string) => id === 'quickbooks' && (qbStarting || qbDisconnecting);

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0">
        {qbMessage && (
          <div className="mb-3"><Alert tone={qbMessage.type === 'success' ? 'success' : 'danger'}>{qbMessage.text}</Alert></div>
        )}
        <div className="space-y-3">
          {integrations.map((intg) => (
            <div
              key={intg.id}
              className="card-surface card-hover flex items-start gap-4 px-5 py-4 cursor-pointer"
              onClick={() => setSelected(intg)}
            >
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-chip"
                style={{ background: `${intg.color}15`, color: intg.color }}
              >
                {intg.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ui-text">{intg.name}</p>
                <p className="text-xs text-ui-text3 mt-0.5 line-clamp-2">{intg.description}</p>
                {intg.id === 'quickbooks' && qbReconnect && (
                  <p className="mt-1 text-xs text-warning-text">{t('settings.quickbooks.reconnectRequired', 'Reconnect required — sync is paused until you reconnect.')}</p>
                )}
                {intg.id === 'quickbooks' && qbConnected && (
                  <p className="mt-1 text-xs text-success-text">
                    {t('settings.quickbooks.connectedLabel', 'Connected')}{qbStatus?.company_name ? ` · ${qbStatus.company_name}` : ''}{qbStatus?.environment ? ` (${qbStatus.environment})` : ''}
                  </p>
                )}
              </div>
              <Button
                variant={isConnected(intg.id) ? 'outline' : 'primary'}
                size="sm"
                disabled={busyFor(intg.id)}
                onClick={(e) => { e.stopPropagation(); toggleConnect(intg.id); }}
              >
                {intg.id === 'quickbooks' && qbReconnect
                  ? t('settings.quickbooks.reconnect', 'Reconnect')
                  : isConnected(intg.id)
                    ? (intg.id === 'quickbooks' ? t('settings.quickbooks.disconnect', 'Disconnect') : t('settings.quickbooks.connectedLabel', 'Connected'))
                    : t('settings.quickbooks.connect', 'Connect')}
              </Button>
            </div>
          ))}
        </div>
        <QuickBooksSyncSettings onReconnect={() => void connectQuickBooks()} />
      </div>

      {/* Right panel */}
      {selected ? (
        <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
          <div className="card-surface p-5 sticky top-4">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-chip mb-3"
              style={{ background: `${selected.color}15`, color: selected.color }}
            >
              {selected.icon}
            </div>
            <p className="text-sm font-semibold text-ui-text mb-2">{selected.name}</p>
            <p className="text-xs text-ui-text2 leading-relaxed">{selected.description}</p>
            <div className="mt-4 space-y-2 text-xs text-ui-text2">
              <p>• View structure</p>
              <p>• Supply time</p>
            </div>
            <Button
              className="mt-5 w-full"
              variant={isConnected(selected.id) ? 'outline' : 'primary'}
              size="sm"
              disabled={busyFor(selected.id)}
              onClick={() => toggleConnect(selected.id)}
            >
              {isConnected(selected.id) ? 'Disconnect' : 'Connect'}
            </Button>
          </div>
        </aside>
      ) : (
        <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
          <div className="card-surface p-5 flex flex-col items-center justify-center min-h-48 text-center">
            <p className="text-sm text-ui-text3">Select an integration to see details.</p>
            <Button variant="secondary" size="sm" className="mt-4">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Need Help?
            </Button>
          </div>
        </aside>
      )}
    </div>
  );
};

// --- Notifications Section ---

const notifGroups = [
  {
    group: 'Tasks',
    items: ['New task assigned'],
  },
  {
    group: 'Workflow',
    items: ['Workflow Transition'],
  },
  {
    group: 'Billing',
    items: ['Invoice Due', 'Payment Received'],
  },
  {
    group: 'Meetings',
    items: ['Meeting Reminder'],
  },
  {
    group: 'Weather',
    items: ['Weather Alert'],
  },
];

const notifSideItems = [
  'Delivery channels',
  'Alerts and updates',
  'Proposal updates',
  'Scheduled reminders',
];

const NotificationsSection: React.FC = () => {
  const [channels, setChannels] = useState({ inApp: true, email: true, sms: false });

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0 space-y-6">
        {/* Delivery channels */}
        <div>
          <p className="text-sm font-semibold text-ui-text mb-3">Delivery Channels</p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-6">
            {[
              { key: 'inApp' as const, label: 'In app' },
              { key: 'email' as const, label: 'Email' },
              { key: 'sms' as const, label: 'SMS' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2">
                <Switch
                  checked={channels[key]}
                  onCheckedChange={() => setChannels((prev) => ({ ...prev, [key]: !prev[key] }))}
                  aria-label={label}
                />
                <span className="text-sm text-ui-text">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Notification groups */}
        <div className="space-y-6">
          {notifGroups.map(({ group, items }) => (
            <div key={group}>
              <p className="eyebrow mb-3">
                {group}
              </p>
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item} className="flex items-center justify-between py-2 border-b border-ui-border last:border-0">
                    <div>
                      <p className="text-sm text-ui-text">{item}</p>
                      <p className="text-xs text-ui-text3 mt-0.5">
                        {group === 'Tasks' ? 'Notify when a task is assigned to you' :
                         group === 'Workflow' ? 'Notify when a task moves to a new stage' :
                         group === 'Billing' ? `Notify for ${item.toLowerCase()} events` :
                         group === 'Meetings' ? 'Notify for an upcoming meeting or appointment' :
                         'Get weather-related workspace alerts'}
                      </p>
                    </div>
                    <Toggle checked />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Button>Save Preferences</Button>
      </div>

      {/* Right panel */}
      <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
        <div className="card-surface p-5">
          <p className="text-sm font-semibold text-ui-text mb-1">Notification Preferences</p>
          <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
            Control when and where you receive notifications to stay informed without being overwhelmed.
          </p>
          <nav className="space-y-1">
            {notifSideItems.map((item, i) => (
              <div key={item} className="flex items-center gap-3 px-3 py-2 rounded-chip text-sm text-ui-text">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-fill text-on-accent text-xs font-semibold">
                  {i + 1}
                </span>
                {item}
              </div>
            ))}
          </nav>
          <Button variant="secondary" size="sm" className="mt-4 w-full">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Need Help?
          </Button>
        </div>
      </aside>
    </div>
  );
};

// --- Pricing / Rate Cards Section ---

interface RateCard {
  id: string;
  name: string;
  markup: string;
  overhead: string;
  tax: string;
  adjPercent: string;
  minJob: string;
  isDefault?: boolean;
}

const defaultRateCards: RateCard[] = [
  {
    id: '1',
    name: 'Standard',
    markup: '20%',
    overhead: '15%',
    tax: '8%',
    adjPercent: '5%',
    minJob: '$150',
    isDefault: true,
  },
];

const PricingSection: React.FC = () => {
  const [cards, setCards] = useState<RateCard[]>(defaultRateCards);
  const [showNew, setShowNew] = useState(false);
  const [newCard, setNewCard] = useState<Partial<RateCard>>({});

  const addCard = (e: React.FormEvent) => {
    e.preventDefault();
    setCards((prev) => [...prev, { id: Date.now().toString(), name: newCard.name ?? '', markup: newCard.markup ?? '', overhead: newCard.overhead ?? '', tax: newCard.tax ?? '', adjPercent: newCard.adjPercent ?? '', minJob: newCard.minJob ?? '' }]);
    setNewCard({});
    setShowNew(false);
  };

  const pricingSideItems = [
    'Add a line',
    'Edit a line',
    'Remove a line',
    'Set as default',
    'Save card',
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0 space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <p className="text-sm font-semibold text-ui-text">Rate Cards</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">Default</Button>
            <Button size="sm" onClick={() => setShowNew(true)}>+ New Card</Button>
          </div>
        </div>

        {/* Rate card table */}
        <div className="table-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {['Markup %', 'Overhead %', 'Contingency %', 'Tax %', 'Adj %', 'Min. Job $'].map((h) => (
                  <th key={h} className="th-cell">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cards.map((card) => (
                <tr key={card.id}>
                  <td className="td-cell">{card.markup}</td>
                  <td className="td-cell">{card.overhead}</td>
                  <td className="td-cell">10%</td>
                  <td className="td-cell">{card.tax}</td>
                  <td className="td-cell">{card.adjPercent}</td>
                  <td className="td-cell">{card.minJob}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showNew && (
          <form onSubmit={addCard} className="card-surface p-5 space-y-4">
            <p className="text-sm font-semibold text-ui-text">+ New Rate Card</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'name', label: 'Name' },
                { key: 'markup', label: 'Markup %' },
                { key: 'overhead', label: 'Overhead %' },
                { key: 'tax', label: 'Tax %' },
                { key: 'adjPercent', label: 'Adj %' },
                { key: 'minJob', label: 'Min. Job $' },
              ].map(({ key, label }) => (
                <FormField key={key} label={label}>
                  <TextInput
                    placeholder={label}
                    value={(newCard as Record<string, string>)[key] ?? ''}
                    onChange={(e) => setNewCard((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                </FormField>
              ))}
            </div>
            <div className="flex gap-3">
              <Button type="submit" size="sm">Save card</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowNew(false)}>Cancel</Button>
            </div>
          </form>
        )}

        {!showNew && (
          <Button variant="link" size="sm" onClick={() => setShowNew(true)}>
            + Add line
          </Button>
        )}
      </div>

      {/* Right panel */}
      <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
        <div className="card-surface p-5">
          <p className="text-sm font-semibold text-ui-text mb-1">Rate Cards &amp; Pricing</p>
          <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
            Manage the rate cards when generating estimates. Each resource can have multiple rate cards,
            but only one can be the active default.
          </p>
          <p className="eyebrow mb-2">How to use this page</p>
          <nav className="space-y-1">
            {pricingSideItems.map((item, i) => (
              <div key={item} className="flex items-center gap-3 px-3 py-2 rounded-chip text-sm text-ui-text">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-fill text-on-accent text-xs font-semibold">
                  {i + 1}
                </span>
                {item}
              </div>
            ))}
          </nav>
          <Alert tone="warning" className="mt-4 leading-relaxed">
            The default rate card is used when generating estimates. You can create multiple
            rate cards —" one can be set as default.
          </Alert>
        </div>
      </aside>
    </div>
  );
};

// --- Workflow Section ---
// The tenant's full workflow configuration — status labels, approvals & gates, who can run each
// transition or invoice action (Authority Assignment) and access policy — in one screen. This is
// the same editor SAM opens as a chat card (WorkflowSettingsCard), so both entry points edit one
// source of truth.
const WorkflowSection: React.FC = () => {
  const { t } = useTranslation();
  const { data: workflows = [] } = useListConfigurableWorkflowsQuery();
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const active = selected ?? workflows.find((w) => w.is_default)?.workflow_id ?? workflows[0]?.workflow_id;
  const current = workflows.find((w) => w.workflow_id === active);
  return (
    <div className="mx-auto max-w-5xl space-y-3">
      {/* Which workflow is being configured — every edit below applies to this one only. */}
      {workflows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="workflow-select" className="eyebrow">{t('settings.workflowSelector.label', 'Workflow')}</label>
          {workflows.length > 1 ? (
            <Select
              id="workflow-select"
              value={active ?? ''}
              onChange={(e) => setSelected(e.target.value)}
              className="min-w-[16rem] max-w-full"
            >
              {workflows.map((w) => (
                <option key={w.workflow_id} value={w.workflow_id}>{w.name}</option>
              ))}
            </Select>
          ) : (
            <span className="text-body font-medium text-ui-text">{current?.name}</span>
          )}
          {current && current.engagement_types.length > 0 && (
            <span className="text-caption text-ui-text3">{t('settings.workflowSelector.appliesTo', 'Applies to: {{types}}', { types: current.engagement_types.join(', ') })}</span>
          )}
        </div>
      )}
      <div className="h-[calc(100dvh-16rem)] min-h-[480px]">
        <WorkflowSettingsCard key={active} workflowId={active} />
      </div>
    </div>
  );
};

// --- Invoice Template Section ---

const InvoiceSection: React.FC = () => {
  const [template, setTemplate] = useState('Classic');
  const [options, setOptions] = useState({
    showLogo: true,
    showDescription: false,
    showLineItems: true,
    showSection: false,
  });
  const [footerText, setFooterText] = useState('Thank you for your business!');

  const invoiceSideItems = [
    'Choose templates',
    'Customize',
    'Add items',
    'Save template',
  ];

  const previewItems = [
    { description: 'Roof Inspection', qty: 1, price: '$500.00', total: '$500.00' },
    { description: 'Shingle Replacement', qty: 2, price: '$750.00', total: '$1,500.00' },
    { description: 'Labor', qty: 8, price: '$75.00', total: '$600.00' },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0 space-y-6">
        <div>
          <p className="text-sm font-semibold text-ui-text mb-0.5">Invoice PDF Template</p>
          <p className="text-xs text-ui-text3">Customize how your invoices appear when sent to customers.</p>
        </div>

        {/* Template selector */}
        <FormField label="Template">
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className={cn(fieldClass({ size: 'sm' }), 'sm:w-48')}
          >
            <option>Classic</option>
            <option>Modern</option>
            <option>Minimal</option>
          </select>
        </FormField>

        {/* Display options */}
        <div>
          <p className="eyebrow mb-3">Display Options</p>
          <div className="space-y-2.5">
            {[
              { key: 'showLogo' as const, label: 'Show logo' },
              { key: 'showDescription' as const, label: 'Show/Hide company description' },
              { key: 'showLineItems' as const, label: 'Include line items' },
              { key: 'showSection' as const, label: 'Hide line section' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <Switch
                  checked={options[key]}
                  onCheckedChange={(next) => setOptions((prev) => ({ ...prev, [key]: next }))}
                  aria-label={label}
                />
                <span className="text-sm text-ui-text">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <FormField label="Footer Text">
          <TextInput
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            placeholder="Thank you for your business!"
          />
        </FormField>

        <Button>Update Template</Button>

        {/* Invoice preview */}
        <div className="card-surface p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between items-start gap-3 sm:gap-0 mb-6">
            <div>
              <p className="text-h3 text-ui-text">INVOICE</p>
              <p className="text-xs text-ui-text3 mt-1">Invoice # INV-2024-001</p>
              <p className="text-xs text-ui-text3">Date: Jun 3, 2025</p>
            </div>
            {options.showLogo && (
              <div className="flex h-12 w-12 items-center justify-center rounded-chip bg-accent-fill text-on-accent font-bold text-sm">
                SC
              </div>
            )}
          </div>
          <div className="mb-4">
            <p className="text-xs font-medium text-ui-text2 mb-1">Bill to:</p>
            <p className="text-sm text-ui-text">John Smith</p>
            <p className="text-xs text-ui-text3">123 Oak Street</p>
            <p className="text-xs text-ui-text3">Raleigh, NC 27601</p>
          </div>
          {options.showLineItems && (
            <div className="overflow-x-auto mb-4">
            <table className="w-full text-xs">
              <thead className="border-b border-ui-border">
                <tr>
                  <th className="text-left pb-2 text-ui-text2 font-medium">Description</th>
                  <th className="text-right pb-2 text-ui-text2 font-medium">Qty</th>
                  <th className="text-right pb-2 text-ui-text2 font-medium">Price</th>
                  <th className="text-right pb-2 text-ui-text2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {previewItems.map((item) => (
                  <tr key={item.description} className="border-b border-ui-border/50">
                    <td className="py-2 text-ui-text">{item.description}</td>
                    <td className="py-2 text-right text-ui-text">{item.qty}</td>
                    <td className="py-2 text-right text-ui-text">{item.price}</td>
                    <td className="py-2 text-right text-ui-text">{item.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
          <div className="flex justify-end">
            <div className="space-y-1 text-xs w-full sm:w-40">
              <div className="flex justify-between text-ui-text2">
                <span>Subtotal</span><span>$2,100.00</span>
              </div>
              <div className="flex justify-between text-ui-text2">
                <span>Tax (8%)</span><span>$78.50</span>
              </div>
              <div className="flex justify-between font-semibold text-ui-text border-t border-ui-border pt-1">
                <span>Total</span><span>$2,278.50</span>
              </div>
            </div>
          </div>
          {footerText && (
            <p className="text-xs text-ui-text3 text-center mt-4 pt-4 border-t border-ui-border">
              {footerText}
            </p>
          )}
        </div>
      </div>

      {/* Right panel */}
      <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
        <div className="card-surface p-5">
          <p className="text-sm font-semibold text-ui-text mb-1">Invoice Customization</p>
          <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
            Customize your invoice template to automatically include your branding and key details.
          </p>
          <p className="eyebrow mb-2">Customization Options</p>
          <nav className="space-y-1 mb-4">
            {invoiceSideItems.map((item, i) => (
              <div key={item} className="flex items-center gap-3 px-3 py-2 rounded-chip text-sm text-ui-text">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-fill text-on-accent text-xs font-semibold">
                  {i + 1}
                </span>
                {item}
              </div>
            ))}
          </nav>
          <Alert tone="info" className="leading-relaxed">
            <strong>Pro tip:</strong> Add your company logo in Brand Settings to automatically
            include it on all invoices.
          </Alert>
        </div>
      </aside>
    </div>
  );
};

// --- Proposal Template Section ---
// Real, wired settings for the generated Proposal PDF (api-service app/domain/proposals/pdf.py) —
// unlike InvoiceSection above (a UI mockup with no backend and no Save handler), this one actually
// loads and saves the `proposal_template` tenant configuration (generic catalogue key, same
// GET/PUT-via-key mechanism `quick_quote_pricing` already uses — see QuickQuotePricingForm.tsx).
// Logo/company name/color are NOT duplicated here: they're read live from the existing 'brand'
// catalogue (Brand Settings tab) both by this preview and by the PDF generator itself, so there is
// exactly one place to change them.
const ProposalTemplateSection: React.FC = () => {
  const { data: brandConfig } = useGetBrandConfigQuery();
  const { data: catalogueData, isLoading, refetch } = useListCatalogueEntriesQuery({ prefix: 'proposal_template' });
  const entry = catalogueData?.data?.find((e) => e.key === 'proposal_template');
  const effective = entry?.effective_value ?? {};

  const [showLogo, setShowLogo] = useState(true);
  const [showCompanyDetails, setShowCompanyDetails] = useState(true);
  const [footerText, setFooterText] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [updateCatalogueConfig, { isLoading: saving }] = useUpdateCatalogueConfigMutation();

  // Seed local editable state from the real saved value exactly once it arrives — mirrors the
  // rest of this file's "load once, then it's the user's local draft" convention (e.g. brand
  // fields below), rather than fighting the user's edits on every background refetch.
  useEffect(() => {
    if (!entry || loadedOnce) return;
    setShowLogo(effective.show_logo !== false);
    setShowCompanyDetails(effective.show_company_details !== false);
    setFooterText(typeof effective.footer_text === 'string' ? effective.footer_text : '');
    setLoadedOnce(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, loadedOnce]);

  const submit = async () => {
    setSaveError('');
    setSaved(false);
    try {
      await updateCatalogueConfig({
        key: 'proposal_template',
        value: { show_logo: showLogo, show_company_details: showCompanyDetails, footer_text: footerText.trim() },
      }).unwrap();
      setSaved(true);
      void refetch();
    } catch (e) {
      setSaveError((e as { data?: { detail?: string } })?.data?.detail || 'Could not save — check your permissions.');
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0 space-y-6">
        <div>
          <p className="text-sm font-semibold text-ui-text mb-0.5">Proposal PDF Template</p>
          <p className="text-xs text-ui-text3">
            Controls what appears on the client-facing Proposal PDF (Download PDF, on the
            Proposal card). Logo, company name, and brand color come from Brand Settings.
          </p>
        </div>

        {isLoading && !loadedOnce ? (
          <LoadingState label="Loading…" className="py-8" />
        ) : (
          <>
            <div>
              <p className="eyebrow mb-3">Display Options</p>
              <div className="space-y-2.5">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Switch checked={showLogo} onCheckedChange={setShowLogo} aria-label="Show logo" />
                  <span className="text-sm text-ui-text">Show logo</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Switch checked={showCompanyDetails} onCheckedChange={setShowCompanyDetails} aria-label="Show company name" />
                  <span className="text-sm text-ui-text">Show company name</span>
                </label>
              </div>
            </div>

            <FormField label="Footer Text" hint="Printed at the bottom of every generated Proposal PDF. Leave blank for no footer line.">
              <TextInput
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                placeholder="Thank you for your business!"
              />
            </FormField>

            {saveError && <Alert tone="danger">{saveError}</Alert>}
            {saved && <Alert tone="success">Saved.</Alert>}
            <Button onClick={() => void submit()} disabled={saving}>
              {saving ? 'Saving…' : 'Save Template Settings'}
            </Button>

            {/* Live preview — same data the real PDF generator (app.domain.proposals.pdf) reads:
                brand.logo_light_url/app_name/primary_color plus these two toggles + footer. */}
            <div className="card-surface p-4 sm:p-6">
              {showLogo && brandConfig?.logo_light_url && (
                <img src={brandConfig.logo_light_url} alt="Logo" className="h-10 mb-3 object-contain" />
              )}
              {showCompanyDetails && brandConfig?.app_name && (
                <p className="text-sm font-semibold mb-1" style={{ color: brandConfig?.primary_color || undefined }}>
                  {brandConfig.app_name}
                </p>
              )}
              <p className="text-h3 text-ui-text">Proposal PRP-1042 · v1</p>
              <p className="text-xs text-ui-text3 mt-1">Prepared for Jane Doe</p>
              <div className="mt-4 pt-4 border-t border-ui-border space-y-1 text-xs">
                <div className="flex justify-between text-ui-text2"><span>Materials</span><span>$6,250</span></div>
                <div className="flex justify-between text-ui-text2"><span>Work &amp; other costs</span><span>$4,761</span></div>
                <div className="flex justify-between font-semibold text-ui-text border-t border-ui-border pt-1">
                  <span>Total</span><span>$12,413</span>
                </div>
              </div>
              {footerText && (
                <p className="text-xs text-ui-text3 text-center mt-4 pt-4 border-t border-ui-border">
                  {footerText}
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
        <div className="card-surface p-5">
          <p className="text-sm font-semibold text-ui-text mb-1">Proposal Customization</p>
          <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
            These settings apply to every Proposal PDF generated for this tenant, immediately.
          </p>
          <Alert tone="info" className="leading-relaxed">
            <strong>Note:</strong> Logo, company name, and color live in Brand Settings — update
            them there and every Proposal PDF (and this preview) picks it up automatically.
          </Alert>
        </div>
      </aside>
    </div>
  );
};

// --- Operations Section ---

const operationsSideItems = [
  'Select Al',
  'Set up email routing',
  'Map lead labels',
  'Go live',
];

const jobTypeColors = ['bg-accent', 'bg-accent', 'bg-success', 'bg-warning', 'bg-danger', 'bg-steel'];
const defaultJobTypes = ['Roofing', 'Gutters', 'Siding', 'Scheduling', 'Communications'];

// ── Teams Section ────────────────────────────────────────────────────────────

const TEAM_COLORS = ['#7C3AED', '#2563EB', '#059669', '#D97706', '#DC2626', '#0891B2', '#DB2777'];

const defaultCrewColor = '#7C3AED';

type TeamModalState = { open: false } | { open: true; editing: Crew | null };

const MEMBER_PAGE_SIZE = 50;

/** A crew-lead option for one member, named from the search results when we have them, else looked up by
 *  id (a member saved on the crew may not be on the current search page). */
const MemberOption: React.FC<{ userId: string; known?: TenantUser }> = ({ userId, known }) => {
  const { t } = useTranslation();
  const { data } = useGetTenantUserByIdQuery(userId, { skip: !!known });
  const u = known ?? data;
  return <option value={userId}>{u ? u.name || u.email : t('common.unknownUser', 'Unknown')}</option>;
};

const TeamsSection: React.FC = () => {
  const { t } = useTranslation();
  const { data: crews = [], isLoading } = useListCrewsQuery();

  const [createCrew] = useCreateCrewMutation();
  const [updateCrew] = useUpdateCrewMutation();
  const [deleteCrew, { isLoading: deleting }] = useDeleteCrewMutation();

  const [modal, setModal] = useState<TeamModalState>({ open: false });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // form state
  const [name, setName] = useState('');
  const [teamType, setTeamType] = useState<'internal' | 'contract'>('internal');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [color, setColor] = useState(defaultCrewColor);
  const [userSearch, setUserSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(userSearch.trim()), 300);
    return () => clearTimeout(id);
  }, [userSearch]);

  // Searched on the server, so a tenant with more users than one page can still find everyone.
  const { data: usersResp, isFetching: fetchingUsers } = useListTenantUsersQuery(
    { search: debouncedSearch || undefined, page_size: MEMBER_PAGE_SIZE },
    { skip: !modal.open || teamType !== 'internal' },
  );
  const tenantUsers: TenantUser[] = useMemo(() => usersResp?.data ?? [], [usersResp]);
  const totalUsers = usersResp?.total ?? tenantUsers.length;
  const knownUsers = useMemo(() => new Map(tenantUsers.map((u) => [u.user_id, u])), [tenantUsers]);

  const resetForm = (crew: Crew | null) => {
    setName(crew?.name ?? '');
    setTeamType((crew?.team_type as 'internal' | 'contract') || 'internal');
    setSelectedMembers(crew?.member_user_ids ?? []);
    setLeadId(crew?.crew_lead_user_id ?? null);
    setContactName(crew?.contact_name ?? '');
    setContactPhone(crew?.contact_phone ?? '');
    setColor(crew?.color ?? defaultCrewColor);
    setUserSearch(''); setError(null);
    setModal({ open: true, editing: crew });
  };

  const close = () => { setModal({ open: false }); setSaving(false); };

  const save = async () => {
    if (!name.trim()) { setError(t('settings.teams.nameRequired', 'Team name is required.')); return; }
    setSaving(true); setError(null);
    const body: CrewBody = {
      name: name.trim(),
      team_type: teamType,
      color: color || defaultCrewColor,
      // A contract crew has no staff, so it has no lead either — clear it, or a crew switched from internal
      // keeps pointing at its old lead.
      ...(teamType === 'internal'
        ? { member_user_ids: selectedMembers, crew_lead_user_id: leadId && selectedMembers.includes(leadId) ? leadId : null }
        : { contact_name: contactName.trim() || null, contact_phone: contactPhone.trim() || null, member_user_ids: [], crew_lead_user_id: null }),
    };
    try {
      if (modal.open && modal.editing) {
        await updateCrew({ crew_id: modal.editing.crew_id, ...body }).unwrap();
      } else {
        await createCrew(body).unwrap();
      }
      close();
    } catch (err) {
      setError(apiErrorMessage(err, t, t('settings.teams.saveFailed', 'Failed to save team. Please try again.')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (crew_id: string) => {
    setDeleteError(null);
    try {
      await deleteCrew(crew_id).unwrap();
      setDeleteConfirm(null);
    } catch (e) {
      setDeleteError(apiErrorMessage(e, t, t('settings.teams.deleteFailed', 'Could not delete the team. Please try again.')));
    }
  };

  const toggleMember = (uid: string) => {
    const removing = selectedMembers.includes(uid);
    setSelectedMembers((prev) => (removing ? prev.filter((x) => x !== uid) : [...prev, uid]));
    if (removing && leadId === uid) setLeadId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-ui-text">{t('settings.teams.title', 'Teams')}</p>
          <p className="text-xs text-ui-text3 mt-0.5">
            {t('settings.teams.intro', 'Internal teams are made up of your own staff. Contract teams are external crews you hire for jobs.')}
          </p>
        </div>
        <Button size="sm" onClick={() => resetForm(null)}>
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {t('settings.teams.add', 'Add team')}
        </Button>
      </div>

      {isLoading && <p className="text-xs text-ui-text3">{t('common.loading', 'Loading...')}</p>}

      {!isLoading && crews.length === 0 && (
        <div className="rounded-xl border border-dashed border-ui-border p-8 text-center">
          <p className="text-sm text-ui-text3">{t('settings.teams.empty', 'No teams yet. Add your first team to get started.')}</p>
        </div>
      )}

      {deleteError && <Alert tone="danger">{deleteError}</Alert>}

      <div className="space-y-2">
        {crews.map((crew) => {
          const memberCount = crew.member_user_ids?.length ?? 0;
          const isInternal = (crew.team_type ?? 'internal') === 'internal';
          return (
            <div key={crew.crew_id} className="flex items-center gap-3 rounded-xl border border-ui-border bg-ui-card px-4 py-3">
              <span
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: crew.color ?? defaultCrewColor }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-ui-text truncate">{crew.name}</span>
                  <Badge variant={isInternal ? 'accent' : 'warning'}>
                    {isInternal ? t('settings.teams.internal', 'Internal') : t('settings.teams.contract', 'Contract')}
                  </Badge>
                </div>
                <p className="text-[11px] text-ui-text3 mt-0.5">
                  {isInternal
                    ? t('settings.teams.memberCount', { count: memberCount, defaultValue: '{{count}} members' })
                    : [crew.contact_name, crew.contact_phone].filter(Boolean).join(' · ') || t('settings.teams.noContact', 'No contact info')}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => resetForm(crew)}
                  className="rounded-lg p-1.5 text-ui-text3 hover:bg-ui-hover hover:text-ui-text transition-colors"
                  title={t('common.edit', 'Edit')}
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                {deleteConfirm === crew.crew_id ? (
                  <div className="flex items-center gap-1">
                    <Button size="xs" variant="destructive-ghost" onClick={() => handleDelete(crew.crew_id)} loading={deleting}>
                      {t('settings.teams.confirmDelete', 'Confirm')}
                    </Button>
                    <Button size="xs" variant="ghost" onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setDeleteConfirm(crew.crew_id); setDeleteError(null); }}
                    className="rounded-lg p-1.5 text-ui-text3 hover:bg-danger/10 hover:text-danger-text transition-colors"
                    title={t('common.delete', 'Delete')}
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modal.open && (
        <Modal
          title={modal.editing ? t('settings.teams.editTitle', 'Edit team') : t('settings.teams.addTitle', 'Add team')}
          onClose={close}
          maxWidthClassName="max-w-md"
          closeOnBackdrop={false}
          onSave={() => void save()}
          saving={saving}
          saveLabel={modal.editing ? t('settings.teams.saveChanges', 'Save changes') : t('settings.teams.create', 'Create team')}
          cancelLabel={t('common.cancel', 'Cancel')}
        >
          {/* Team name */}
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">{t('settings.teams.name', 'Team name')}</label>
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.teams.namePlaceholder', 'e.g. Alpha Crew')}
            />
          </div>

          {/* Type toggle */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-ui-text">{t('settings.teams.type', 'Team type')}</label>
            <div className="flex gap-2">
              {(['internal', 'contract'] as const).map((tt) => (
                <button
                  key={tt}
                  type="button"
                  onClick={() => setTeamType(tt)}
                  className={cn(
                    'flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                    teamType === tt
                      ? 'border-accent bg-accent-fill text-on-accent'
                      : 'border-ui-border text-ui-text2 hover:border-accent/40',
                  )}
                >
                  {tt === 'internal' ? t('settings.teams.typeInternal', 'Internal (our staff)') : t('settings.teams.typeContract', 'Contract (external)')}
                </button>
              ))}
            </div>
          </div>

          {/* Internal: member picker + crew lead */}
          {teamType === 'internal' && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-ui-text">{t('settings.teams.members', 'Members')}</label>
                <TextInput
                  placeholder={t('settings.teams.searchUsers', 'Search users…')}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="mb-2 text-xs"
                />
                <div className="max-h-36 overflow-y-auto space-y-1 rounded-lg border border-ui-border p-1.5">
                  {!fetchingUsers && tenantUsers.length === 0 && (
                    <p className="py-2 text-center text-[11px] text-ui-text3">{t('settings.teams.noUsers', 'No users found')}</p>
                  )}
                  {tenantUsers.map((u) => (
                    <label key={u.user_id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-ui-hover">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(u.user_id)}
                        onChange={() => toggleMember(u.user_id)}
                        className="shrink-0 accent-accent-fill"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-ui-text truncate">{u.name || u.email}</p>
                        {u.role_names && u.role_names.length > 0 && (
                          <p className="text-[10px] text-ui-text3 truncate">{u.role_names.join(', ')}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                {totalUsers > tenantUsers.length && (
                  <p className="mt-1 text-[10px] text-ui-text3">
                    {t('settings.teams.moreUsers', 'Showing {{shown}} of {{total}} users — search to find others.', { shown: tenantUsers.length, total: totalUsers })}
                  </p>
                )}
                {selectedMembers.length > 0 && (
                  <p className="mt-1 text-[10px] text-ui-text3">{t('settings.teams.selectedCount', '{{count}} selected', { count: selectedMembers.length })}</p>
                )}
              </div>

              <div>
                <label htmlFor="crew-lead" className="mb-1 block text-xs font-medium text-ui-text">{t('settings.teams.lead', 'Crew lead')}</label>
                <Select
                  id="crew-lead"
                  value={leadId ?? ''}
                  onChange={(e) => setLeadId(e.target.value || null)}
                  disabled={selectedMembers.length === 0}
                >
                  <option value="">{t('settings.teams.noLead', 'No crew lead')}</option>
                  {selectedMembers.map((uid) => (
                    <MemberOption key={uid} userId={uid} known={knownUsers.get(uid)} />
                  ))}
                </Select>
                {selectedMembers.length === 0 && (
                  <p className="mt-1 text-[10px] text-ui-text3">{t('settings.teams.leadHint', 'Add members first, then pick which one leads the crew.')}</p>
                )}
              </div>
            </div>
          )}

          {/* Contract: contact info */}
          {teamType === 'contract' && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-ui-text">{t('settings.teams.contactName', 'Contact name')}</label>
                <TextInput
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder={t('settings.teams.contactNamePlaceholder', 'e.g. Mike Johnson')}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ui-text">{t('settings.teams.contactPhone', 'Contact phone')}</label>
                <TextInput
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder={t('settings.teams.contactPhonePlaceholder', 'e.g. +1 555-000-0000')}
                  type="tel"
                />
              </div>
            </div>
          )}

          {/* Color picker */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-ui-text">{t('settings.teams.color', 'Calendar color')}</label>
            <div className="flex gap-2 flex-wrap">
              {TEAM_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  aria-label={c}
                  aria-pressed={color === c}
                  className={cn(
                    'h-6 w-6 rounded-full transition-all',
                    color === c ? 'ring-2 ring-offset-2 ring-ui-ring scale-110' : 'hover:scale-105',
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {error && <p className="text-xs text-danger-text">{error}</p>}
        </Modal>
      )}
    </div>
  );
};

const OperationsSection: React.FC = () => {
  const [jobTypes, setJobTypes] = useState<string[]>(defaultJobTypes);
  const [newJobType, setNewJobType] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [emailRoutes] = useState([
    { domain: 'support@', status: 'Live default' },
    { domain: 'Estimates', status: 'Live default' },
    { domain: 'Maintenance', status: 'Live default' },
    { domain: 'Jobs', status: 'Live default' },
    { domain: 'Schedule Batch', status: 'Live default' },
    { domain: 'All Articles Published', status: 'Live default' },
  ]);

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex-1 min-w-0 space-y-8">
        {/* Job types */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-ui-text">Job types</p>
            <Button size="sm">Save</Button>
          </div>
          <p className="text-xs text-ui-text3 mb-4">
            Define the job types or trade areas your business handles.
          </p>
          <div className="flex gap-2 flex-wrap mb-3">
            {jobTypes.map((jt, i) => (
              <span
                key={jt}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-white',
                  jobTypeColors[i % jobTypeColors.length]
                )}
              >
                {jt}
                <IconButton
                  label={`Remove ${jt}`}
                  size="xs"
                  onClick={() => setJobTypes((prev) => prev.filter((j) => j !== jt))}
                  className="h-4 w-4 text-white hover:bg-white/20"
                >
                  ×
                </IconButton>
              </span>
            ))}
            <div className="flex items-center gap-1">
              <TextInput
                placeholder="Add type"
                value={newJobType}
                onChange={(e) => setNewJobType(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newJobType.trim()) {
                    e.preventDefault();
                    setJobTypes((prev) => [...prev, newJobType.trim()]);
                    setNewJobType('');
                  }
                }}
                fullWidth={false}
                className="w-20 sm:w-28 text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (newJobType.trim()) {
                    setJobTypes((prev) => [...prev, newJobType.trim()]);
                    setNewJobType('');
                  }
                }}
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Email integration */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-ui-text">Email Integration/routing</p>
            <Button size="sm">Save</Button>
          </div>
          <p className="text-xs text-ui-text3 mb-4">
            Set up email forwarding or configure your Inbox to route emails to categories for
            processing.
          </p>
          <div className="flex gap-2 mb-3 flex-wrap">
            {['LabelEmail', 'Forward'].map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
          </div>
          <FormField label="Provide the specific email routing configuration (optional)">
            <TextInput
              placeholder="Example: Forward all emails with subject-containing, body matching, or tags"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
            />
          </FormField>
          {/* Email routes table */}
          <div className="table-card mt-4 overflow-x-auto">
            <div className="px-4 py-2.5 border-b border-ui-border bg-ui-subtle">
              <p className="eyebrow">Connected Email Routes</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="th-cell">Domain</th>
                  <th className="th-cell">Status</th>
                </tr>
              </thead>
              <tbody>
                {emailRoutes.map((route) => (
                  <tr key={route.domain}>
                    <td className="td-cell text-xs">{route.domain}</td>
                    <td className="td-cell text-xs text-ui-text3">{route.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <aside className="w-full lg:w-72 shrink-0 order-first lg:order-none">
        <div className="card-surface p-5">
          <p className="text-sm font-semibold text-ui-text mb-1">SAM Configuration</p>
          <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
            Configure SAM AI capabilities and address scheduling and customer notifications.
          </p>
          <p className="eyebrow mb-2">Setup guide</p>
          <nav className="space-y-1">
            {operationsSideItems.map((item, i) => (
              <div key={item} className="flex items-center gap-3 px-3 py-2 rounded-chip text-sm text-ui-text">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-fill text-on-accent text-xs font-semibold">
                  {i + 1}
                </span>
                {item}
              </div>
            ))}
          </nav>
          <Alert tone="info" className="mt-4 leading-relaxed">
            <strong>Note:</strong> Once you've configured it first, it can be used in
            customer-facing emails based on your label.
          </Alert>
        </div>
      </aside>
    </div>
  );
};

// --- Top Tab Bar ---

// Each tab declares the permission required to see it.
// No `permission` field = visible to all authenticated tenant users.
const allTopTabs: { key: TopTab; label: string; permission?: string }[] = [
  { key: 'brand',         label: 'Brand Settings',   permission: 'tenant:settings:write' },
  { key: 'team',          label: 'Team & Roles',      permission: 'tenant:settings:write' },
  { key: 'sam',           label: 'SAM Settings' },
  { key: 'integrations',  label: 'Integrations',      permission: 'tenant:settings:write' },
  { key: 'notifications', label: 'Notifications',     permission: 'tenant:settings:write' },
  { key: 'pricing',       label: 'Pricing',           permission: 'tenant:settings:write' },
  // The one screen for materials, services and their supplier/price catalogue (folded in the
  // former standalone "Inventory" tab) — estimate:write, the same permission the underlying
  // tenant-preferences/inventory endpoints already enforce (root CLAUDE.md's Configuration
  // Catalogue reasoning: gate on what the API itself checks, not a bespoke settings permission).
  { key: 'material_service_management', label: 'Material & Service Management', permission: 'estimate:write' },
  { key: 'teams',         label: 'Teams',             permission: 'tenant:settings:write' },
  { key: 'workflow',      label: 'Workflow',          permission: 'tenant:settings:write' },
  { key: 'invoice',       label: 'Invoice Template',  permission: 'tenant:settings:write' },
  // Follows the estimate/proposal permission pair (proposal:write, added alongside the real
  // Proposal PDF export) rather than tenant:settings:write — same reasoning as inventory above:
  // whoever prepares proposals maintains this, and it's the same permission the underlying
  // catalogue write is already gated on nowhere else but here.
  { key: 'proposal_template', label: 'Proposal Template', permission: 'proposal:write' },
  // The CONTENT a proposal is built from (payment terms, T&C clauses, exclusions, warranty), as
  // opposed to the PDF layout the tab above controls. Same permission for the same reason.
  { key: 'proposal_content', label: 'Proposal Content', permission: 'proposal:write' },
  { key: 'operations',    label: 'Operations',        permission: 'tenant:settings:write' },
];

// --- Main Page ---

const TenantSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetTenantSettingsQuery();
  const [updateProfile, { isLoading: savingProfile }] = useUpdateTenantMutation();

  const permissions = useAppSelector((state) => state.sessionState.permissions);
  const isTenantAdmin = permissions.includes('tenant:settings:write');

  // Reaching the settings page is the final onboarding milestone. If the user arrives here
  // straight out of the guided wizard (stage still 'settings'), record it —" so subsequent
  // logins route to the dashboard, not back into onboarding —" then move them on to the
  // dashboard. Guarded by a ref so it fires once and doesn't re-trigger on later visits.
  const { data: onboardingState } = useGetOnboardingStateQuery(undefined, { skip: !isTenantAdmin });
  const [markSettingsVisited] = useMarkSettingsVisitedMutation();
  const onboardingCompletedRef = useRef(false);
  useEffect(() => {
    if (onboardingState?.data?.stage !== 'settings' || onboardingCompletedRef.current) return;
    onboardingCompletedRef.current = true;
    markSettingsVisited()
      .unwrap()
      .catch(() => {})
      .finally(() => navigate('/chat', { replace: true }));
  }, [onboardingState, markSettingsVisited, navigate]);

  // Show tabs the current user has the required permission for
  const visibleTabs = useMemo(
    () => allTopTabs.filter((t) => !t.permission || permissions.includes(t.permission)),
    [permissions]
  );

  // ?tab=integrations is where the Intuit OAuth redirect lands after connecting QuickBooks.
  const [activeTab, setActiveTab] = useState<TopTab>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'integrations' ? 'integrations'
      : isTenantAdmin ? 'brand' : 'sam'
  );
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const settings = (data?.data as Record<string, unknown>) ?? {};
  const isSaving = savingProfile;

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async (_section: string, payload: Record<string, unknown>) => {
    try {
      await updateProfile(payload).unwrap();
      showToast('success', 'Changes saved successfully.');
    } catch {
      showToast('error', 'Failed to save changes. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingState label="Loading settings…" />
      </div>
    );
  }

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <PageHeader
          title="Settings"
          toolbar={
            <Tabs
              tabs={visibleTabs.map((tab) => ({ id: tab.key, label: tab.label }))}
              value={activeTab}
              onChange={(id) => setActiveTab(id as TopTab)}
              label="Settings sections"
            />
          }
        />
      }
    >
      {/* Toast */}
      {toast && (
        <div
          className={cn(
            'fixed top-4 right-4 z-toast flex items-center gap-3 rounded-control px-4 py-3 shadow-pop text-sm text-white transition-all',
            toast.type === 'success' ? 'bg-success-fill' : 'bg-danger-fill'
          )}
        >
          {toast.type === 'success' ? (
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {toast.msg}
        </div>
      )}

      {/* Content */}
      <div className="pt-6">
        {activeTab === 'brand' && (
          <BrandSettingsSection settings={settings} onSave={handleSave} saving={isSaving} />
        )}
        {activeTab === 'team' && <TeamSection />}
        {activeTab === 'sam' && <SAMSection isTenantAdmin={isTenantAdmin} />}
        {activeTab === 'integrations' && <IntegrationsSection />}
        {activeTab === 'notifications' && <NotificationsSection />}
        {activeTab === 'pricing' && <PricingSection />}
        {activeTab === 'material_service_management' && <MaterialServiceManagementSection />}
        {activeTab === 'teams' && <TeamsSection />}
        {activeTab === 'workflow' && <WorkflowSection />}
        {activeTab === 'invoice' && <InvoiceSection />}
        {activeTab === 'proposal_template' && <ProposalTemplateSection />}
        {activeTab === 'proposal_content' && <ProposalContentSection />}
        {activeTab === 'operations' && <OperationsSection />}
      </div>
    </PageShell>
  );
};

export default TenantSettingsPage;
